import React, { useState, useEffect, useRef } from 'react';
import {
  Settings, Save, RefreshCw, Download, Upload, Shield, Database,
  Mail, Key, Activity, AlertTriangle, CheckCircle, Clock, Trash2,
  Edit2, Plus, Search, Eye, EyeOff, Copy, Play, Pause, RotateCcw,
  Server, HardDrive, Wifi, Bell, Globe, Lock, FileText, Calendar, Printer,
  Volume2, Music, RotateCcw as Recovery
} from 'lucide-react';
import { soundManager } from '../utils/soundManager';
// Mock data removed - using database data
import {
  SystemSettings, BackupConfiguration, BackupLog, SystemLog,
  EmailConfiguration, ApiKey
} from '../types';
import { apiService } from '../services/api';
import Modal from './Modal';
import { useLanguage } from '../contexts/LanguageContext';

const SystemSettingsPage: React.FC = () => {
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState('general');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'setting' | 'backup' | 'email' | 'api' | 'cron'>('setting');
  const [editingItem, setEditingItem] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPasswords, setShowPasswords] = useState<{[key: number]: boolean}>({});
  const [showApiKeys, setShowApiKeys] = useState<{[key: number]: boolean}>({});
  const [systemSettings, setSystemSettings] = useState<SystemSettings[]>([]);
  const [backupConfigs, setBackupConfigs] = useState<any[]>([]);
  const [backupLogs, setBackupLogs] = useState<any[]>([]);
  const [emailConfigs, setEmailConfigs] = useState<any[]>([]);
  const [cronJobs, setCronJobs] = useState<any[]>([]);
  const [systemLogs, setSystemLogs] = useState<any[]>([]);
  const [loginLogs, setLoginLogs] = useState<any[]>([]);
  const [blockedAccounts, setBlockedAccounts] = useState<any[]>([]);
  const [adminUsers, setAdminUsers] = useState<any[]>([]);
  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Logo, Favicon, Title ve Login Background için state'ler
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [faviconFile, setFaviconFile] = useState<File | null>(null);
  const [loginBackgroundFile, setLoginBackgroundFile] = useState<File | null>(null);
  const [systemTitle, setSystemTitle] = useState('IT Toolbox');
  const [orderCapexLimit, setOrderCapexLimit] = useState<number>(200);
  const [currentLogo, setCurrentLogo] = useState<string | null>(null);
  const [currentFavicon, setCurrentFavicon] = useState<string | null>(null);
  const [currentLoginBackground, setCurrentLoginBackground] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [uploadingLoginBackground, setUploadingLoginBackground] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [cronScripts, setCronScripts] = useState<{ script: string; label: string }[]>([]);
  const [cronData, setCronData] = useState({
    job_name: '',
    cron_expression: '0 2 * * *',
    command: '',
    script: '' as string,
    description: '',
    is_active: true,
    notification_recipients: [] as number[]
  });

  const [settingData, setSettingData] = useState({
    setting_key: '',
    setting_value: '',
    setting_type: 'string' as 'string' | 'number' | 'boolean' | 'json',
    category: 'general' as 'general' | 'email' | 'backup' | 'security' | 'notification' | 'integration',
    description: '',
    is_encrypted: false
  });

  const [backupData, setBackupData] = useState({
    backup_name: '',
    backup_type: 'database' as 'database' | 'files' | 'system' | 'full',
    source_path: '',
    destination_path: '',
    schedule_cron: '0 2 * * *',
    retention_days: 30,
    compression_enabled: true,
    encryption_enabled: false,
    status: 'active' as 'active' | 'paused' | 'disabled',
    notification_emails: [''],
    notes: ''
  });

  const [emailData, setEmailData] = useState({
    config_name: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_encryption: 'tls' as 'none' | 'tls' | 'ssl',
    from_email: '',
    from_name: '',
    is_default: false,
    is_active: true,
    test_email: '',
    daily_limit: 1000,
    monthly_limit: 30000,
    description: ''
  });

  const [apiData, setApiData] = useState({
    key_name: '',
    permissions: [''],
    rate_limit_per_hour: 1000,
    allowed_ips: [''],
    expires_at: '',
    is_active: true
  });

  // Bulk print states
  const [bulkPrintData, setBulkPrintData] = useState({
    location_id: 0,
    print_type: 'cabinet', // 'cabinet' or 'switch'
    label_width: 100,
    label_height: 50,
    include_switches: true,
    orientation: 'horizontal', // 'horizontal' or 'vertical'
    margin_top: 2,
    margin_right: 2,
    margin_bottom: 2,
    margin_left: 2
  });
  const [bulkPrintItems, setBulkPrintItems] = useState([]);
  const [showBulkPrintPreview, setShowBulkPrintPreview] = useState(false);
  const [locationsData, setLocationsData] = useState<any[]>([]);
  
  // Recovery states
  const [recoveryFile, setRecoveryFile] = useState<File | null>(null);
  const [backupAnalysis, setBackupAnalysis] = useState<any>(null);
  const [selectedTables, setSelectedTables] = useState<string[]>([]);
  const [recoveryProgress, setRecoveryProgress] = useState<number>(0);
  const [isRecovering, setIsRecovering] = useState(false);
  const [recoveryLogs, setRecoveryLogs] = useState<string[]>([]);
  const [backupSource, setBackupSource] = useState<'upload' | 'server'>('upload');
  const [serverBackups, setServerBackups] = useState<any[]>([]);
  const [selectedServerBackup, setSelectedServerBackup] = useState<string>('');

  // Sound settings states
  const [soundSettings, setSoundSettings] = useState(soundManager.getSettings());
  const [soundFiles, setSoundFiles] = useState<any[]>([]);
  const [activeSound, setActiveSound] = useState<string | null>(null);
  const [uploadingSound, setUploadingSound] = useState(false);
  const soundFileInputRef = useRef<HTMLInputElement>(null);

  const tabs = [
    { id: 'general', label: t('sysSettings.tabGeneral'), icon: Settings },
    { id: 'recovery', label: t('sysSettings.tabRecovery'), icon: Recovery },
    { id: 'email', label: t('sysSettings.tabEmail'), icon: Mail },
    { id: 'cron', label: t('sysSettings.tabCron'), icon: Clock },
    { id: 'orders', label: t('sysSettings.tabOrders'), icon: FileText },
    { id: 'security', label: t('sysSettings.tabSecurity'), icon: Shield },
    { id: 'logs', label: t('sysSettings.tabLogs'), icon: Activity },
    { id: 'login-logs', label: t('sysSettings.tabLoginLogs'), icon: Lock },
    { id: 'api', label: t('sysSettings.tabApi'), icon: Key },
    { id: 'bulk-print', label: t('sysSettings.tabBulkPrint'), icon: Printer },
    { id: 'sounds', label: t('sysSettings.tabSounds'), icon: Volume2 }
  ];

  const settingCategories = [
    { value: 'general', label: t('sysSettings.catGeneral'), color: 'bg-primary/20 text-primary-dark' },
    { value: 'email', label: t('sysSettings.catEmail'), color: 'bg-green-100 text-green-800' },
    { value: 'backup', label: t('sysSettings.catBackup'), color: 'bg-purple-100 text-purple-800' },
    { value: 'security', label: t('sysSettings.catSecurity'), color: 'bg-red-100 text-red-800' },
    { value: 'notification', label: t('sysSettings.catNotification'), color: 'bg-yellow-100 text-yellow-800' },
    { value: 'integration', label: t('sysSettings.catIntegration'), color: 'bg-indigo-100 text-indigo-800' }
  ];

  const logLevels = [
    { value: 'debug', label: 'Debug', color: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200' },
    { value: 'info', label: 'Info', color: 'bg-primary/20 text-primary-dark' },
    { value: 'warning', label: 'Warning', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'error', label: 'Error', color: 'bg-red-100 text-red-800' },
    { value: 'critical', label: 'Critical', color: 'bg-red-200 text-red-900' }
  ];

  const handleEdit = (item: any, type: 'setting' | 'backup' | 'email' | 'api' | 'cron') => {
    setEditingItem(item);
    setModalType(type);
    
    if (type === 'setting') {
      setSettingData({
        setting_key: item.setting_key,
        setting_value: item.setting_value,
        setting_type: item.setting_type,
        category: item.category,
        description: item.description,
        is_encrypted: item.is_encrypted
      });
    } else if (type === 'backup') {
      setBackupData({
        backup_name: item.backup_name,
        backup_type: item.backup_type,
        source_path: item.source_path,
        destination_path: item.destination_path,
        schedule_cron: item.schedule_cron,
        retention_days: item.retention_days,
        compression_enabled: item.compression_enabled,
        encryption_enabled: item.encryption_enabled,
        status: item.status,
        notification_emails: item.notification_emails || [''],
        notes: item.notes || ''
      });
    } else if (type === 'email') {
      setEmailData({
        config_name: item.name, // Backend sends 'name', we use 'config_name' in frontend
        smtp_host: item.smtp_host,
        smtp_port: item.smtp_port,
        smtp_username: item.smtp_username,
        smtp_password: item.smtp_password,
        smtp_encryption: item.smtp_encryption,
        from_email: item.from_email,
        from_name: item.from_name,
        is_default: item.is_default,
        is_active: item.is_active,
        test_email: item.test_email || '',
        daily_limit: item.daily_limit || 1000,
        monthly_limit: item.monthly_limit || 30000,
        description: item.description || ''
      });
    } else if (type === 'api') {
      setApiData({
        key_name: item.key_name,
        permissions: (typeof item.permissions === 'string' ? JSON.parse(item.permissions) : item.permissions) || [''],
        rate_limit_per_hour: item.rate_limit_per_hour,
        allowed_ips: (typeof item.allowed_ips === 'string' ? JSON.parse(item.allowed_ips) : item.allowed_ips) || [''],
        expires_at: item.expires_at ? item.expires_at.split('T')[0] : '',
        is_active: item.is_active
      });
    } else if (type === 'cron') {
      setCronData({
        job_name: item.job_name,
        cron_expression: item.cron_expression,
        command: item.command,
        script: item.script || '',
        description: item.description,
        is_active: item.is_active,
        notification_recipients: item.notification_recipients || []
      });
    }
    
    setShowModal(true);
  };

  const handleAdd = (type: 'setting' | 'backup' | 'email' | 'api' | 'cron') => {
    setEditingItem(null);
    setModalType(type);
    
    if (type === 'setting') {
      setSettingData({
        setting_key: '',
        setting_value: '',
        setting_type: 'string',
        category: 'general',
        description: '',
        is_encrypted: false
      });
    } else if (type === 'backup') {
      setBackupData({
        backup_name: '',
        backup_type: 'database',
        source_path: '',
        destination_path: '',
        schedule_cron: '0 2 * * *',
        retention_days: 30,
        compression_enabled: true,
        encryption_enabled: false,
        status: 'active',
        notification_emails: [''],
        notes: ''
      });
    } else if (type === 'email') {
      setEmailData({
        config_name: '',
        smtp_host: '',
        smtp_port: 587,
        smtp_username: '',
        smtp_password: '',
        smtp_encryption: 'tls',
        from_email: '',
        from_name: '',
        is_default: false,
        is_active: true,
        test_email: '',
        daily_limit: 1000,
        monthly_limit: 30000,
        description: ''
      });
    } else if (type === 'api') {
      setApiData({
        key_name: '',
        permissions: [''],
        rate_limit_per_hour: 1000,
        allowed_ips: [''],
        expires_at: '',
        is_active: true
      });
    } else if (type === 'cron') {
      setCronData({
        job_name: '',
        cron_expression: '0 2 * * *',
        command: '',
        script: '',
        description: '',
        is_active: true,
        notification_recipients: []
      });
    }
    
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (modalType === 'setting') {
        if (editingItem) {
          await apiService.updateSystemSetting(editingItem.id, settingData);
        } else {
          await apiService.createSystemSetting(settingData);
        }
        await fetchSystemSettings();
      } else if (modalType === 'backup') {
        if (editingItem) {
          await apiService.updateBackupConfig(editingItem.id, backupData);
        } else {
          await apiService.createBackupConfig(backupData);
        }
        await fetchBackupConfigs();
      } else if (modalType === 'email') {
        // Convert frontend field names to backend field names
        const backendEmailData = {
          name: emailData.config_name, // config_name -> name
          smtp_host: emailData.smtp_host,
          smtp_port: emailData.smtp_port,
          smtp_username: emailData.smtp_username,
          smtp_password: emailData.smtp_password,
          smtp_encryption: emailData.smtp_encryption,
          from_email: emailData.from_email,
          from_name: emailData.from_name,
          is_default: emailData.is_default,
          is_active: emailData.is_active,
          description: emailData.description || null
        };

        if (editingItem) {
          await apiService.updateEmailConfig(editingItem.id, backendEmailData);
        } else {
          await apiService.createEmailConfig(backendEmailData);
        }
        await fetchEmailConfigs();
      } else if (modalType === 'cron') {
        if (editingItem) {
          await apiService.updateCronJob(editingItem.id, cronData);
        } else {
          await apiService.createCronJob(cronData);
        }
        await fetchCronJobs();
      }
      setShowModal(false);
    } catch (error) {
      console.error('Save error:', error);
      setError(error instanceof Error ? error.message : t('sysSettings.settingUpdated'));
    }
  };

  const handleDelete = async (id: number, type: string) => {
    if (confirm(t('sysSettings.cronDeleteConfirm'))) {
      try {
        if (type === 'setting') {
          await apiService.deleteSystemSetting(id);
          await fetchSystemSettings();
        } else if (type === 'email') {
          await apiService.deleteEmailConfig(id);
          await fetchEmailConfigs();
        } else if (type === 'cron') {
          await apiService.deleteCronJob(id);
          await fetchCronJobs();
        } else if (type === 'backup') {
          await apiService.deleteBackupConfig(id);
          await fetchBackupConfigs();
        }
      } catch (error) {
        console.error('Delete error:', error);
        setError(error instanceof Error ? error.message : t('sysSettings.cronDeleteError'));
      }
    }
  };

  const runBackup = async (backup: any) => {
    try {
      setError(null);
      await apiService.runBackup(backup.id);
      await fetchBackupLogs(); // Refresh backup logs
      alert(t('sysSettings.backupStarted'));
    } catch (error) {
      console.error('Backup run error:', error);
      setError(error instanceof Error ? error.message : t('sysSettings.backupRunError'));
    }
  };

  const testEmailConfig = async (config: any) => {
    const testEmail = prompt(t('sysSettings.emailTestPrompt'));
    if (!testEmail) return;
    
    try {
      setError(null);
      const result = await apiService.testEmailConfig(config.id, testEmail);
      alert(`✓ ${t('sysSettings.emailTestTitle')}!\n\n${testEmail}\n\nSMTP:\n- ${result.details?.smtp_host}:${result.details?.smtp_port}\n- ${result.details?.encryption?.toUpperCase()}`);
      await fetchEmailConfigs(); // Refresh to show updated last_tested
    } catch (error: any) {
      console.error('Email test error:', error);
      const errorMsg = error.message || t('sysSettings.cronDeleteError');
      alert(`✗ ${t('sysSettings.cronDeleteError')}\n\n${errorMsg}`);
      setError(errorMsg);
    }
  };

  const generateApiKey = () => {
    const key = 'itb_live_' + Math.random().toString(36).substring(2, 18);
    setApiData({...apiData, key_name: key});
  };

  // Recovery Functions
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.sql.gz')) {
      setError(t('sysSettings.onlySqlGz'));
      return;
    }

    setRecoveryFile(file);
    setBackupAnalysis(null);
    setSelectedTables([]);
    setRecoveryLogs([]);
  };

  const fetchServerBackups = async () => {
    try {
      // Backup dizinindeki dosyaları listele
      const response = await fetch('/api/system/list-backups', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setServerBackups(data.backups || []);
      } else {
        setError(`${t('sysSettings.serverBackupListError')} ${response.status}`);
        setServerBackups([]);
      }
    } catch (error: any) {
      setError(`${t('sysSettings.serverBackupListLoadError')} ${error.message || t('sysSettings.unknownError')}`);
      setServerBackups([]);
    }
  };

  const handleServerBackupSelect = (filename: string) => {
    setSelectedServerBackup(filename);
    setBackupAnalysis(null);
    setSelectedTables([]);
    setRecoveryLogs([]);
  };

  const analyzeServerBackup = async () => {
    if (!selectedServerBackup) return;

    try {
      setLoading(true);
      setError(null);
      setRecoveryLogs([t('sysSettings.analyzing')]);

      const response = await fetch('/api/system/analyze-server-backup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ filename: selectedServerBackup })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('sysSettings.serverBackupListError'));
      }

      const analysis = await response.json();
      setBackupAnalysis(analysis);
      setRecoveryLogs(prev => [
        ...prev,
        `✅ ${t('sysSettings.recoverySuccess')}`,
        `📊 ${t('sysSettings.tablesFound').replace('{count}', String(analysis.tables?.length || 0))}`,
        `💾 ${(analysis.fileSize / 1024 / 1024).toFixed(2)} MB`
      ]);
    } catch (error: any) {
      const errorMsg = error.message || t('sysSettings.unknownError');
      setError(errorMsg);
      setRecoveryLogs(prev => [...prev, `❌ ${errorMsg}`]);
    } finally {
      setLoading(false);
    }
  };

  const analyzeBackupFile = async () => {
    if (!recoveryFile) return;

    try {
      setLoading(true);
      setError(null);
      setRecoveryLogs([t('sysSettings.backupFileAnalyzing')]);

      const formData = new FormData();
      formData.append('backup_file', recoveryFile);

      const response = await fetch('/api/system/analyze-backup', {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('sysSettings.serverBackupListError'));
      }

      const analysis = await response.json();
      setBackupAnalysis(analysis);
      setRecoveryLogs(prev => [
        ...prev,
        `✅ ${t('sysSettings.recoverySuccess')}`,
        `📊 ${t('sysSettings.tablesFound').replace('{count}', String(analysis.tables?.length || 0))}`,
        `💾 ${(analysis.fileSize / 1024 / 1024).toFixed(2)} MB`
      ]);
    } catch (error: any) {
      const errorMsg = error.message || t('sysSettings.unknownError');
      setError(errorMsg);
      setRecoveryLogs(prev => [...prev, `❌ ${errorMsg}`]);
    } finally {
      setLoading(false);
    }
  };

  const handleTableSelection = (table: string, checked: boolean) => {
    if (checked) {
      setSelectedTables(prev => [...prev, table]);
    } else {
      setSelectedTables(prev => prev.filter(t => t !== table));
    }
  };

  const startRecovery = async () => {
    if ((!recoveryFile && !selectedServerBackup) || selectedTables.length === 0) return;

    const tableList = selectedTables.join(', ');
    if (!confirm(t('sysSettings.reloadConfirm').replace('{count}', String(selectedTables.length)).replace('{tables}', tableList))) {
      return;
    }

    try {
      setIsRecovering(true);
      setRecoveryProgress(10);
      setRecoveryLogs([t('sysSettings.restoring')]);

      let response;

      if (backupSource === 'server') {
        // Server backup restore
        setRecoveryProgress(30);
        setRecoveryLogs(prev => [...prev, `🖥️ ${t('sysSettings.serverBackup')}...`]);

        response = await fetch('/api/system/restore-server-backup', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify({
            filename: selectedServerBackup,
            selected_tables: selectedTables
          })
        });
      } else {
        // File upload restore
        const formData = new FormData();
        if (recoveryFile) {
          formData.append('backup_file', recoveryFile);
        }
        formData.append('selected_tables', JSON.stringify(selectedTables));

        setRecoveryProgress(30);
        setRecoveryLogs(prev => [...prev, `📤 ${t('sysSettings.uploadFile')}...`]);

        response = await fetch('/api/system/restore-backup', {
          method: 'POST',
          body: formData,
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        });
      }

      setRecoveryProgress(60);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('sysSettings.recoveryTitle'));
      }

      setRecoveryLogs(prev => [...prev, `⚡ ${t('sysSettings.restoring')}...`]);
      setRecoveryProgress(80);

      const result = await response.json();
      setRecoveryProgress(100);
      setRecoveryLogs(prev => [
        ...prev,
        `✅ ${t('sysSettings.recoverySuccessAlert')}`,
        `📊 ${t('sysSettings.tablesRestored').replace('{count}', String(result.restoredTables || 0))}`,
        `📝 ${t('sysSettings.recordsProcessed').replace('{count}', String(result.restoredRecords || 0))}`,
        `⏰ ${t('sysSettings.completedAt')}: ${new Date().toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}`
      ]);

      // Show success message
      alert(`✅ ${t('sysSettings.recoverySuccessAlert')}\n\n${t('sysSettings.recoverySuccessMsg').replace('{tables}', String(result.restoredTables)).replace('{records}', String(result.restoredRecords))}`);

      // Reset form after successful recovery
      setTimeout(() => {
        setRecoveryFile(null);
        setSelectedServerBackup('');
        setBackupAnalysis(null);
        setSelectedTables([]);
        setRecoveryProgress(0);
        
        // Refresh the page to show updated data
        if (confirm(t('sysSettings.reloadConfirm'))) {
          window.location.reload();
        }
      }, 2000);

    } catch (error: any) {
      const errorMsg = error.message || t('sysSettings.unknownError');
      setError(errorMsg);
      setRecoveryLogs(prev => [...prev, `❌ ${errorMsg}`]);
      alert(`❌ ${errorMsg}`);
    } finally {
      setIsRecovering(false);
    }
  };

  const togglePassword = (id: number) => {
    setShowPasswords(prev => ({...prev, [id]: !prev[id]}));
  };

  const toggleApiKey = (id: number) => {
    setShowApiKeys(prev => ({...prev, [id]: !prev[id]}));
  };

  const getCategoryColor = (category: string) => {
    return settingCategories.find(c => c.value === category)?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  };

  const getLogLevelColor = (level: string) => {
    return logLevels.find(l => l.value === level)?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  };

  const getBackupStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'disabled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const getLogStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'running': return 'bg-primary/20 text-primary-dark';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const addEmailToList = (list: string[], setList: (list: string[]) => void) => {
    setList([...list, '']);
  };

  const removeEmailFromList = (index: number, list: string[], setList: (list: string[]) => void) => {
    if (list.length > 1) {
      setList(list.filter((_, i) => i !== index));
    }
  };

  const updateEmailInList = (index: number, value: string, list: string[], setList: (list: string[]) => void) => {
    const newList = [...list];
    newList[index] = value;
    setList(newList);
  };

  const fetchSystemSettings = async () => {
    try {
      setLoading(true);
      const data = await apiService.getSystemSettings();
      
      // Data'nın array olduğundan emin ol
      const settingsArray = Array.isArray(data) ? data : [];
      setSystemSettings(settingsArray);
      
      // System title'ı setting'lerden al
      const titleSetting = settingsArray.find((s: any) => s.setting_key === 'system_title');
      if (titleSetting) {
        setSystemTitle(titleSetting.setting_value);
      }

      // Order CAPEX limit'i setting'lerden al
      const capexLimitSetting = settingsArray.find((s: any) => s.setting_key === 'order_capex_limit');
      if (capexLimitSetting) {
        setOrderCapexLimit(parseFloat(capexLimitSetting.setting_value) || 200);
      }

      // System logo'yu setting'lerden al
      const logoSetting = settingsArray.find((s: any) => s.setting_key === 'system_logo');
      if (logoSetting && logoSetting.setting_value) {
        const timestamp = new Date().getTime();
        const logoUrl = `${logoSetting.setting_value}?t=${timestamp}`;
        setCurrentLogo(logoUrl);
      } else {
        // Settings'de yoksa dosyayı kontrol et
        try {
          const timestamp = new Date().getTime();
          const logoResponse = await fetch(`/uploads/logo.png?t=${timestamp}`);
          if (logoResponse.ok) {
            setCurrentLogo(`/uploads/logo.png?t=${timestamp}`);
          } else {
            setCurrentLogo(null);
          }
        } catch (error) {
          setCurrentLogo(null);
        }
      }
      
      setError(null);
    } catch (error) {
      console.error('Error fetching system settings:', error);
      setError(error instanceof Error ? error.message : t('sysSettings.loadingSettings'));
    } finally {
      setLoading(false);
    }
  };

  // Logo yükleme fonksiyonu
  const handleLogoUpload = async () => {
    if (!logoFile) return;
    
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', logoFile);
      
      const response = await apiService.uploadLogo(formData);
      setLogoFile(null);
      
      // Backend'den dönen path'i kullan - timestamp ile cache bypass
      const timestamp = new Date().getTime();
      const logoPath = response.path || '/uploads/logo.png';
      setCurrentLogo(`${logoPath}?t=${timestamp}`);
      
      // System settings'i yeniden yükle
      await fetchSystemSettings();
      
      // Global event dispatch et - sidebar'ı güncellemek için
      window.dispatchEvent(new CustomEvent('logoUpdated', { 
        detail: { logoPath: `${logoPath}?t=${timestamp}` } 
      }));
      
      // Başarı mesajı göster
      alert(t('sysSettings.logoUploaded'));
      
    } catch (error) {
      console.error('Logo upload error:', error);
      setError(error instanceof Error ? error.message : t('sysSettings.logoDeleteError'));
    } finally {
      setUploadingLogo(false);
    }
  };

  // Login Background yükleme fonksiyonu
  const handleLoginBackgroundUpload = async () => {
    if (!loginBackgroundFile) return;
    
    setUploadingLoginBackground(true);
    try {
      const formData = new FormData();
      formData.append('login_background', loginBackgroundFile);
      
      const response = await apiService.uploadLoginBackground(formData);
      setLoginBackgroundFile(null);
      
      // Backend'den dönen path'i kullan - timestamp ile cache bypass
      const timestamp = new Date().getTime();
      const backgroundPath = response.path || '/uploads/login-background.jpg';
      setCurrentLoginBackground(`${backgroundPath}?t=${timestamp}`);
      
      // System settings'i yeniden yükle
      await fetchSystemSettings();
      
      // Global event dispatch et
      window.dispatchEvent(new CustomEvent('loginBackgroundUpdated', { 
        detail: { backgroundPath: `${backgroundPath}?t=${timestamp}` } 
      }));
      
      // Başarı mesajı göster
      alert(t('sysSettings.loginBgUploaded'));
      
    } catch (error) {
      console.error('Login background upload error:', error);
      setError(error instanceof Error ? error.message : t('sysSettings.loginBgDeleteError'));
    } finally {
      setUploadingLoginBackground(false);
    }
  };

  // Favicon yükleme fonksiyonu
  const handleFaviconUpload = async () => {
    if (!faviconFile) return;
    
    setUploadingFavicon(true);
    try {
      const formData = new FormData();
      formData.append('favicon', faviconFile);
      
      const response = await apiService.uploadFavicon(formData);
      setFaviconFile(null);
      
      // Backend'den dönen path'i kullan - timestamp ile cache bypass
      const timestamp = new Date().getTime();
      const faviconPath = response.path || '/uploads/favicon.ico';
      setCurrentFavicon(`${faviconPath}?t=${timestamp}`);
      
      // Browser favicon'unu güncelle
      updateBrowserFavicon(faviconPath, timestamp);
      
      // System settings'i yeniden yükle
      await fetchSystemSettings();
      
      // Global event dispatch et
      window.dispatchEvent(new CustomEvent('faviconUpdated', { 
        detail: { faviconPath: `${faviconPath}?t=${timestamp}` } 
      }));
      
      // Başarı mesajı göster
      alert(t('sysSettings.faviconUploaded'));
      
    } catch (error) {
      console.error('Favicon upload error:', error);
      setError(error instanceof Error ? error.message : t('sysSettings.logoDeleteError'));
    } finally {
      setUploadingFavicon(false);
    }
  };

  // Browser favicon'unu güncelle
  const updateBrowserFavicon = (faviconPath: string, timestamp: number) => {
    // Existing favicon link'leri bul ve kaldır
    const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
    existingFavicons.forEach(favicon => favicon.remove());
    
    // Yeni favicon link'i oluştur
    const faviconLink = document.createElement('link');
    faviconLink.rel = 'icon';
    faviconLink.type = faviconPath.endsWith('.ico') ? 'image/x-icon' : 'image/png';
    faviconLink.href = `${faviconPath}?t=${timestamp}`;
    
    // Head'e ekle
    document.head.appendChild(faviconLink);
    
    // Apple touch icon da ekle
    const appleTouchIcon = document.createElement('link');
    appleTouchIcon.rel = 'apple-touch-icon';
    appleTouchIcon.href = `${faviconPath}?t=${timestamp}`;
    document.head.appendChild(appleTouchIcon);
  };

  // System title güncelleme fonksiyonu
  const handleTitleUpdate = async () => {
    try {
      // System title setting'ini güncelle/oluştur
      const titleData = {
        setting_key: 'system_title',
        setting_value: systemTitle,
        setting_type: 'string',
        category: 'general',
        description: 'Sistem başlığı',
        is_encrypted: false
      };

      // Upsert kullanarak güvenli bir şekilde kaydet
      await apiService.upsertSystemSetting('system_title', titleData);

      alert(t('sysSettings.titleUpdated'));

      // Tüm bileşenlerin sistem ayarlarını yeniden yüklemesi için event dispatch et
      window.dispatchEvent(new CustomEvent('systemSettingsUpdated'));

    } catch (error) {
      console.error('Title update error:', error);
      alert(t('sysSettings.titleUpdateError'));
    }
  };

  const fetchBackupConfigs = async () => {
    try {
      const data = await apiService.getBackupConfigs();
      setBackupConfigs(data);
    } catch (error) {
      console.error('Error fetching backup configs:', error);
      setError(error instanceof Error ? error.message : t('sysSettings.backupPlans'));
    }
  };

  const fetchBackupLogs = async () => {
    try {
      const data = await apiService.getBackupLogs();
      setBackupLogs(data);
    } catch (error) {
      console.error('Error fetching backup logs:', error);
      setError(error instanceof Error ? error.message : t('sysSettings.noBackupLogs'));
    }
  };

  const fetchEmailConfigs = async () => {
    try {
      const data = await apiService.getEmailConfigs();
      setEmailConfigs(data);
    } catch (error) {
      console.error('Error fetching email configs:', error);
      setError(error instanceof Error ? error.message : t('sysSettings.noEmailConfigs'));
    }
  };

  const fetchCronJobs = async () => {
    try {
      const [data, scripts] = await Promise.all([apiService.getCronJobs(), apiService.getCronScripts()]);
      setCronJobs(data);
      setCronScripts(scripts);
    } catch (error) {
      console.error('Error fetching cron jobs:', error);
      setError(error instanceof Error ? error.message : t('sysSettings.noCronJobs'));
    }
  };

  const fetchAdminUsers = async () => {
    try {
      const data = await apiService.getAdminUsers();
      setAdminUsers(data);
    } catch (error) {
      console.error('Error fetching admin users:', error);
      setError(error instanceof Error ? error.message : t('sysSettings.unknownError'));
    }
  };

  const fetchBlockedAccounts = async () => {
    try {
      const data = await apiService.getBlockedAccounts();
      setBlockedAccounts(data);
    } catch (error) {
      console.error('Error fetching blocked accounts:', error);
      setError(error instanceof Error ? error.message : t('sysSettings.noBlockedAccounts'));
    }
  };

  const fetchApiKeys = async () => {
    try {
      const data = await apiService.getApiKeys();
      setApiKeys(data);
    } catch (error) {
      console.error('Error fetching API keys:', error);
      setError(error instanceof Error ? error.message : t('sysSettings.noApiKeys'));
    }
  };

  const fetchSystemLogs = async () => {
    try {
      const data = await apiService.getSystemLogs();
      setSystemLogs(data.logs || []);
    } catch (error) {
      console.error('Error fetching system logs:', error);
      setError(error instanceof Error ? error.message : t('sysSettings.noSystemLogs'));
    }
  };

  const fetchSounds = async () => {
    try {
      const data = await apiService.getSounds();
      setSoundFiles(data.files || []);
      setActiveSound(data.activeSound || null);
      // Sync active sound to soundManager
      if (data.activeSound) {
        const url = `${import.meta.env.VITE_API_URL?.replace('/api', '')}/uploads/sounds/${data.activeSound}`;
        soundManager.setCustomSoundUrl(url);
      }
    } catch (error) {
      console.error('Error fetching sounds:', error);
    }
  };

  const handleSoundUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingSound(true);
    try {
      const formData = new FormData();
      formData.append('sound', file);
      await apiService.uploadSound(formData);
      setSuccessMessage(t('sysSettings.soundUploadSuccess'));
      await fetchSounds();
    } catch (error) {
      setError(t('sysSettings.soundUploadError'));
    } finally {
      setUploadingSound(false);
      if (soundFileInputRef.current) soundFileInputRef.current.value = '';
    }
  };

  const handleSetActiveSound = async (filename: string | null) => {
    try {
      await apiService.setActiveSound(filename);
      setActiveSound(filename);
      if (filename) {
        const url = `${import.meta.env.VITE_API_URL?.replace('/api', '')}/uploads/sounds/${filename}`;
        soundManager.setCustomSoundUrl(url);
      } else {
        soundManager.setCustomSoundUrl(null);
        setSoundSettings(soundManager.getSettings());
      }
      setSuccessMessage(t('sysSettings.soundActivated'));
    } catch (error) {
      setError(error instanceof Error ? error.message : t('sysSettings.soundUploadError'));
    }
  };

  const handleDeleteSound = async (filename: string) => {
    if (!confirm(t('sysSettings.soundDeleteConfirm'))) return;
    try {
      await apiService.deleteSound(filename);
      setSuccessMessage(t('sysSettings.soundDeleteSuccess'));
      await fetchSounds();
    } catch (error) {
      setError(error instanceof Error ? error.message : t('sysSettings.soundUploadError'));
    }
  };

  const handlePreviewSound = (url: string) => {
    const audio = new Audio(url);
    audio.volume = soundSettings.volume;
    audio.play().catch(() => {});
  };

  const fetchLoginLogs = async () => {
    try {
      const [logsData, blockedData] = await Promise.all([
        apiService.getLoginLogs(),
        apiService.getBlockedAccounts()
      ]);
      // Son kayıtları önce göster
      setLoginLogs(logsData.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()));
      setBlockedAccounts(blockedData);
    } catch (error) {
      console.error('Error fetching login logs:', error);
      setError(error instanceof Error ? error.message : t('sysSettings.noLoginLogs'));
    }
  };

  const handleUnblockAccount = async (email: string) => {
    if (confirm(t('sysSettings.unblockConfirm').replace('{email}', email))) {
      try {
        await apiService.unblockAccount(email);
        await fetchLoginLogs();
        setError(null);
      } catch (error) {
        console.error('Error unblocking account:', error);
        setError(error instanceof Error ? error.message : t('sysSettings.unblock'));
      }
    }
  };

  const loadBulkPrintData = async () => {
    try {
      if (bulkPrintData.location_id === 0) {
        setBulkPrintItems([]);
        return;
      }
      
      if (bulkPrintData.print_type === 'cabinet') {
        const cabinets = await apiService.getCabinets();
        const filteredCabinets = cabinets.filter(c => c.location_id === bulkPrintData.location_id);
        setBulkPrintItems(filteredCabinets);
      } else {
        const switches = await apiService.getNetworkSwitches();
        const filteredSwitches = switches.filter(s => {
          return s.location_id == bulkPrintData.location_id;
        });
        setBulkPrintItems(filteredSwitches);
      }
    } catch (error) {
      console.error('Error loading bulk print data:', error);
      setError(t('sysSettings.bulkPrintLoadError'));
    }
  };

  const printBulkLabels = async () => {
    if (bulkPrintItems.length === 0) {
      setError(t('sysSettings.noPrintItems'));
      return;
    }
    
    try {
      const printWindow = window.open('', '_blank');
      if (!printWindow) return;
      
      let labelsHTML = '';
      
      for (const item of bulkPrintItems) {
        if (bulkPrintData.print_type === 'cabinet') {
          const detailedCabinet = await apiService.getCabinet((item as any).id);
          labelsHTML += generateCabinetLabelHTML(detailedCabinet, bulkPrintData);
        } else {
          labelsHTML += generateSwitchLabelHTML(item, bulkPrintData);
        }
      }
      
      const fullHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${t('sysSettings.bulkPrintTitle')}</title>
          <style>
            @page { size: A4; margin: 10mm; }
            @media print {
              html, body { margin: 0; padding: 0; }
              .page-break { page-break-before: always; }
            }
            body { font-family: Arial, sans-serif; }
            .labels-container {
              display: flex;
              flex-wrap: wrap;
              gap: 5mm;
            }
          </style>
        </head>
        <body>
          <div class="labels-container">
            ${labelsHTML}
          </div>
        </body>
        </html>
      `;
      
      printWindow.document.write(fullHTML);
      printWindow.document.close();
      
      setTimeout(() => {
        printWindow.print();
      }, 1000);
      
    } catch (error) {
      console.error('Error printing bulk labels:', error);
      setError(t('sysSettings.bulkPrintError'));
    }
  };

  const generateCabinetLabelHTML = (cabinet, settings) => {
    const isVertical = settings.orientation === 'vertical';
    const width = isVertical ? settings.label_height : settings.label_width;
    const height = isVertical ? settings.label_width : settings.label_height;
    
    return `
      <div style="
        width: ${width}mm;
        height: ${height}mm;
        border: 2px solid #2563eb;
        border-radius: 4px;
        padding: 3mm;
        background: white;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        margin: 2mm;
        page-break-inside: avoid;
      ">
        <div style="text-align: center; border-bottom: 1px solid #2563eb; padding-bottom: 2mm;">
          <div style="font-size: 20px; font-weight: bold; color: #1e40af; margin-bottom: 1mm;">
            <span style="width: 3mm; height: 3mm; background: #2563eb; border-radius: 1mm; display: inline-block; margin-right: 1mm;"></span>
            ${cabinet.cabinet_name}
          </div>
          <div style="font-size: 12px; color: #64748b; font-weight: bold;">
            <strong>${cabinet.company_name || 'Şirket Belirtilmemiş'}</strong> • <strong>${cabinet.location_name || 'Lokasyon Belirtilmemiş'}</strong>
          </div>
        </div>
        
        <div style="flex-grow: 1; display: flex; flex-direction: column; justify-content: center; margin: 2mm 0;">
          ${settings.include_switches && cabinet.switches && cabinet.switches.length > 0 ? `
            <div style="text-align: center;">
              <div style="font-size: 12px; font-weight: bold; color: #374151; margin-bottom: 2mm; text-transform: uppercase;">Network Switchleri</div>
              ${cabinet.switches.slice(0, 3).map(sw => `
                <div style="display: flex; justify-content: space-between; align-items: center; padding: 1mm 2mm; margin-bottom: 1mm; background: #f8fafc; border-radius: 2mm; border-left: 2px solid #10b981; font-size: 8px;">
                  <div style="display: flex; flex-direction: column; text-align: left;">
                    <span style="font-weight: bold; color: #111827; font-size: 11px;">${sw.switch_name}</span>
                    <span style="font-size: 9px; font-weight: bold; color: #6b7280; margin-top: 0.5mm;">${sw.brand} ${sw.model}</span>
                  </div>
                  <span style="color: #1e40af; font-family: 'Courier New', monospace; font-weight: bold; background: #dbeafe; padding: 0.5mm 1mm; border-radius: 1mm; font-size: 10px;">${sw.ip_address}</span>
                </div>
              `).join('')}
              ${cabinet.switches.length > 3 ? `<div style="font-size: 8px; color: #6b7280; margin-top: 1mm;">+${cabinet.switches.length - 3} switch daha...</div>` : ''}
            </div>
          ` : `
            <div style="text-align: center; color: #000000; font-size: 16px; font-weight: bold; padding: 2mm; background: #f9fafb; border-radius: 2mm; border: 1px dashed #d1d5db;">
              Kabin Etiketi
            </div>
          `}
        </div>
        
        <div style="text-align: center; font-size: 14px; font-weight: bold; color: #000000; border-top: 1px solid #d1d5db; padding-top: 1mm;">
          <span style="width: 3mm; height: 3mm; background: #2563eb; border-radius: 1mm; display: inline-block; margin-right: 1mm;"></span>
          IT Toolbox - ${new Date().toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}
        </div>
      </div>
    `;
  };

  const generateSwitchLabelHTML = (switchItem, settings) => {
    const isVertical = settings.orientation === 'vertical';
    const width = isVertical ? settings.label_height : settings.label_width;
    const height = isVertical ? settings.label_width : settings.label_height;
    
    return `
      <div style="
        width: ${width}mm;
        height: ${height}mm;
        border: 2px solid #059669;
        border-radius: 3mm;
        padding: 3mm;
        background: white;
        display: flex;
        flex-direction: row;
        align-items: center;
        margin: 2mm;
        page-break-inside: avoid;
      ">
        <div style="flex: 1; ${isVertical ? 'padding-bottom: 3mm;' : 'padding-right: 3mm;'}">
          <div style="font-size: 18px; font-weight: bold; color: #065f46; margin-bottom: 2mm;">
            <span style="width: 4mm; height: 4mm; background: #10b981; border-radius: 1mm; display: inline-block; margin-right: 2mm;"></span>
            ${switchItem.switch_name}
          </div>
          <div style="font-size: 14px; font-weight: bold; color: #374151; font-family: 'Courier New', monospace; background: #f0fdf4; padding: 1mm 2mm; border-radius: 2mm; display: inline-block; border: 1px solid #10b981; margin-bottom: 2mm;">
            ${switchItem.ip_address}
          </div>
          
          <div style="display: grid; grid-template-columns: 1fr; gap: 1mm;">
            <div style="background: #f9fafb; padding: 1.5mm; border-radius: 2mm; border-left: 2px solid #10b981;">
              <div style="font-size: 10px; color: #6b7280; text-transform: uppercase; font-weight: bold; margin-bottom: 0.5mm;">Model</div>
              <div style="font-size: 11px; color: #111827; font-weight: bold;">${switchItem.brand} ${switchItem.model}</div>
            </div>
            
            <div style="background: #f9fafb; padding: 1.5mm; border-radius: 2mm; border-left: 2px solid #10b981;">
              <div style="font-size: 10px; color: #6b7280; text-transform: uppercase; font-weight: bold; margin-bottom: 0.5mm;">Port</div>
              <div style="font-size: 11px; color: #111827; font-weight: bold;">${switchItem.port_count}P</div>
            </div>
            
            <div style="background: #f9fafb; padding: 1.5mm; border-radius: 2mm; border-left: 2px solid #10b981;">
              <div style="font-size: 10px; color: #6b7280; text-transform: uppercase; font-weight: bold; margin-bottom: 0.5mm;">Lokasyon</div>
              <div style="font-size: 11px; color: #111827; font-weight: bold;">${switchItem.location?.name || 'N/A'}</div>
            </div>
          </div>
        </div>
        
        <div style="width: 20mm; text-align: center; border-left: 1px solid #d1d5db; padding-left: 2mm;">
          <div style="width: 15mm; height: 15mm; background: #e5e7eb; border: 1px solid #d1d5db; border-radius: 2mm; display: inline-block; margin-bottom: 2mm;"></div>
          <div style="font-size: 12px; font-weight: bold; color: #000000;">
            <strong>IT Toolbox</strong><br>
            <strong>${new Date().toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}</strong>
          </div>
        </div>
      </div>
    `;
  };

  const fetchLocations = async () => {
    try {
      const data = await apiService.getLocations();
      setLocationsData(data);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setError(error instanceof Error ? error.message : t('sysSettings.unknownError'));
    }
  };

  // Mevcut logo ve favicon'u yükle
  const loadCurrentAssets = async () => {
    try {
      // System settings'den logo ve favicon path'lerini al
      const settings = await apiService.getSystemSettings();
      if (Array.isArray(settings)) {
        const logoSetting = settings.find(s => s.setting_key === 'system_logo');
        const faviconSetting = settings.find(s => s.setting_key === 'system_favicon');
        
        if (logoSetting && logoSetting.setting_value) {
          const timestamp = new Date().getTime();
          setCurrentLogo(`${logoSetting.setting_value}?t=${timestamp}`);
        } else {
          // Settings'de yoksa dosyayı kontrol et
          try {
            const logoResponse = await fetch('/uploads/logo.png');
            if (logoResponse.ok) {
              setCurrentLogo('/uploads/logo.png');
            }
          } catch (error) {
            // Logo yok
          }
        }
        
        if (faviconSetting && faviconSetting.setting_value) {
          const timestamp = new Date().getTime();
          setCurrentFavicon(`${faviconSetting.setting_value}?t=${timestamp}`);
        } else {
          // Settings'de yoksa dosyayı kontrol et
          try {
            const faviconResponse = await fetch('/uploads/favicon.ico');
            if (faviconResponse.ok) {
              setCurrentFavicon('/uploads/favicon.ico');
            }
          } catch (error) {
            // Favicon yok
          }
        }

        // Login background yükle
        const loginBgSetting = settings.find(s => s.setting_key === 'login_background');
        if (loginBgSetting && loginBgSetting.setting_value) {
          const timestamp = new Date().getTime();
          setCurrentLoginBackground(`${loginBgSetting.setting_value}?t=${timestamp}`);
        } else {
          // Settings'de yoksa dosyayı API ile kontrol et
          try {
            const bgResponse = await fetch('/api/system-settings/login-background-exists', {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              }
            });

            if (bgResponse.ok) {
              const bgData = await bgResponse.json();
              if (bgData.exists && bgData.path) {
                const timestamp = new Date().getTime();
                setCurrentLoginBackground(`${bgData.path}?t=${timestamp}`);
              }
            }
          } catch (error) {
            // Login background yok
          }
        }
      }
    } catch (error) {
      console.error('Error loading current assets:', error);
      setError(error instanceof Error ? error.message : t('sysSettings.unknownError'));
    }
  };


  useEffect(() => {
    fetchSystemSettings();
    fetchLocations();
    fetchAdminUsers(); // Admin users'ı her zaman yükle (cron modal için)
    loadCurrentAssets(); // Mevcut logo ve favicon'u yükle
    if (activeTab === 'backup') {
      fetchBackupConfigs();
      fetchBackupLogs();
    } else if (activeTab === 'email') {
      fetchEmailConfigs();
    } else if (activeTab === 'cron') {
      fetchCronJobs();
    } else if (activeTab === 'logs') {
      fetchSystemLogs();
    } else if (activeTab === 'login-logs') {
      fetchLoginLogs();
    } else if (activeTab === 'security') {
      fetchBlockedAccounts();
      fetchAdminUsers();
    } else if (activeTab === 'api') {
      fetchApiKeys();
    } else if (activeTab === 'bulk-print') {
      loadBulkPrintData();
    } else if (activeTab === 'recovery') {
      fetchServerBackups();
    } else if (activeTab === 'sounds') {
      fetchSounds();
      setSoundSettings(soundManager.getSettings());
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'bulk-print') {
      loadBulkPrintData();
    }
  }, [bulkPrintData.location_id, bulkPrintData.print_type]);

  if (loading) {
    return (
      <div className="p-3 sm:p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-indigo-600"></div>
          <span className="ml-2 text-sm sm:text-base text-gray-600 dark:text-gray-400">{t('sysSettings.loadingSettings')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('sysSettings.title')}</h1>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">{t('sysSettings.pageSubtitle')}</p>
        {error && (
          <div className="mt-2 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-2 sm:space-x-8 overflow-x-auto">
          {tabs.map((tab) => {
            const IconComponent = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-3 px-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
                }`}
              >
                <IconComponent className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* General Settings Tab */}
      {activeTab === 'general' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('sysSettings.generalTitle')}</h2>
            <button
              onClick={() => handleAdd('setting')}
              className="w-full sm:w-auto flex items-center justify-center px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors min-h-[44px] text-sm sm:text-base"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('sysSettings.newSetting')}
            </button>
          </div>

          {/* Logo, Favicon ve Title Yönetimi */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-6 flex items-center">
                <Globe className="w-5 h-5 mr-2 text-primary" />
                {t('sysSettings.systemAppearance')}
              </h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* System Title */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('sysSettings.systemTitle')}
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={systemTitle}
                      onChange={(e) => setSystemTitle(e.target.value)}
                      className="flex-1 px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary min-h-[44px]"
                      placeholder="IT Toolbox"
                    />
                    <button
                      onClick={handleTitleUpdate}
                      className="px-4 py-3 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors flex items-center min-h-[44px] text-sm sm:text-base"
                    >
                      <Save className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Logo Upload */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('sysSettings.systemLogo')}
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          // Dosya validasyonu
                          const maxSize = 2 * 1024 * 1024; // 2MB
                          const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml', 'image/gif', 'image/webp'];
                          
                          if (file.size > maxSize) {
                            setError(t('sysSettings.errFileTooLarge2MB'));
                            e.target.value = '';
                            return;
                          }

                          if (!allowedTypes.includes(file.type)) {
                            setError(t('sysSettings.errInvalidImageType'));
                            e.target.value = '';
                            return;
                          }
                          
                          setLogoFile(file);
                          setError(null);
                          
                          // Otomatik yükleme
                          setUploadingLogo(true);
                          try {
                            const formData = new FormData();
                            formData.append('logo', file);
                            const response = await apiService.uploadLogo(formData);
                            
                            // Backend'den dönen path'i kullan - timestamp ile cache bypass
                            const timestamp = new Date().getTime();
                            const logoPath = response.path || '/uploads/logo.png';
                            const logoUrl = `${logoPath}?t=${timestamp}`;
                            setCurrentLogo(logoUrl);
                            
                            // System settings'i yeniden yükle
                            await fetchSystemSettings();
                            
                            // Global event dispatch et - tüm sayfaları güncellemek için
                            window.dispatchEvent(new CustomEvent('logoUpdated', { 
                              detail: { logoPath: logoUrl } 
                            }));
                            
                            // Force reload logo in all components
                            setTimeout(() => {
                              window.dispatchEvent(new CustomEvent('logoUpdated', { 
                                detail: { logoPath: logoUrl } 
                              }));
                            }, 100);
                            
                            setError(null);
                            setSuccessMessage(t('sysSettings.logoUploaded'));
                            
                            // Başarı mesajını 3 saniye sonra temizle
                            setTimeout(() => {
                              setSuccessMessage(null);
                            }, 3000);
                            
                            // Input'u temizle
                            e.target.value = '';
                            setLogoFile(null);
                            
                          } catch (error) {
                            console.error('Logo upload error:', error);
                            let errorMessage = t('sysSettings.logoDeleteError');

                            if (error instanceof Error) {
                              if (error.message.includes('413')) {
                                errorMessage = t('sysSettings.errFileTooLarge2MB');
                              } else if (error.message.includes('415')) {
                                errorMessage = t('sysSettings.errInvalidImageType');
                              } else if (error.message.includes('401')) {
                                errorMessage = t('sysSettings.unknownError');
                              } else if (error.message.includes('500')) {
                                errorMessage = t('sysSettings.unknownError');
                              } else {
                                errorMessage = error.message;
                              }
                            }
                            
                            setError(errorMessage);
                            // Hata durumunda da input'u temizle
                            e.target.value = '';
                            setLogoFile(null);
                          } finally {
                            setUploadingLogo(false);
                          }
                        }
                      }}
                      className="flex-1 text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary-dark hover:file:bg-primary/20"
                      disabled={uploadingLogo}
                    />
                    {uploadingLogo && (
                      <RefreshCw className="w-4 h-4 animate-spin text-green-600" />
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('sysSettings.logoHint')}</p>
                  
                  {/* Hata mesajı */}
                  {error && (
                    <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                      <div className="flex items-center">
                        <AlertTriangle className="w-4 h-4 text-red-600 mr-2" />
                        <span className="text-sm text-red-800">{error}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Başarı mesajı */}
                  {successMessage && (
                    <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-md">
                      <div className="flex items-center">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                        <span className="text-sm text-green-800">{successMessage}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Mevcut Logo Preview */}
                  {currentLogo && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('sysSettings.currentLogo')}:
                      </label>
                      <div className="flex items-center space-x-3">
                        <img 
                          src={currentLogo} 
                          alt="Mevcut Logo" 
                          className="h-12 w-auto border border-gray-200 dark:border-gray-700 rounded"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                        <button
                          onClick={async () => {
                            if (confirm(t('sysSettings.removeLogoConfirm'))) {
                              try {
                                await apiService.deleteLogo();
                                setCurrentLogo(null);
                                
                                // System settings'i yeniden yükle
                                await fetchSystemSettings();
                                
                                // Global event dispatch et - tüm sayfaları güncellemek için
                                window.dispatchEvent(new CustomEvent('logoUpdated', { 
                                  detail: { logoPath: null } 
                                }));
                                
                                setError(null);
                                setSuccessMessage(t('sysSettings.logoDeleted'));
                                
                                // Başarı mesajını 3 saniye sonra temizle
                                setTimeout(() => {
                                  setSuccessMessage(null);
                                }, 3000);
                              } catch (error) {
                                console.error('Logo silme hatası:', error);
                                setError(t('sysSettings.logoDeleteError'));
                              }
                            }
                          }}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          {t('sysSettings.removeLogo')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Favicon Upload */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('sysSettings.favicon')}
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="file"
                      accept=".ico,.png"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setFaviconFile(file);
                          // Otomatik yükleme
                          setUploadingFavicon(true);
                          try {
                            const formData = new FormData();
                            formData.append('favicon', file);
                            await apiService.uploadFavicon(formData);
                            setFaviconFile(null);
                            
                            // Mevcut favicon'u güncelle
                            const timestamp = new Date().getTime();
                            setCurrentFavicon(`/uploads/favicon.ico?t=${timestamp}`);
                            
                            // Sayfa favicon'unu güncelle
                            const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
                            if (link) {
                              link.href = `/uploads/favicon.ico?t=${timestamp}`;
                            } else {
                              const newLink = document.createElement('link');
                              newLink.rel = 'icon';
                              newLink.href = `/uploads/favicon.ico?t=${timestamp}`;
                              document.head.appendChild(newLink);
                            }
                            
                            // System settings'i yeniden yükle
                            await fetchSystemSettings();
                            
                            // Global event dispatch et
                            window.dispatchEvent(new CustomEvent('faviconUpdated', { 
                              detail: { faviconPath: `/uploads/favicon.ico?t=${timestamp}` } 
                            }));
                            
                            alert(t('sysSettings.faviconUploaded'));
                          } catch (error) {
                            console.error('Favicon upload error:', error);
                            setError(error instanceof Error ? error.message : t('sysSettings.faviconUploaded'));
                          } finally {
                            setUploadingFavicon(false);
                          }
                        }
                      }}
                      className="flex-1 text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                      disabled={uploadingFavicon}
                    />
                    {uploadingFavicon && (
                      <RefreshCw className="w-4 h-4 animate-spin text-purple-600" />
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('sysSettings.faviconHint')}</p>
                </div>
              </div>

              {/* Önizleme */}
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">{t('sysSettings.currentView')}</h4>
                <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="w-8 h-8 bg-primary rounded-md flex items-center justify-center overflow-hidden">
                    {currentLogo ? (
                      <img 
                        src={currentLogo} 
                        alt="Logo" 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <Globe className="w-5 h-5 text-white" style={{display: currentLogo ? 'none' : 'flex'}} />
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{systemTitle}</span>
                </div>
                
                {/* Favicon Önizleme */}
                {currentFavicon && (
                  <div className="mt-3 flex items-center space-x-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Favicon:</span>
                    <img 
                      src={currentFavicon} 
                      alt="Favicon" 
                      className="w-4 h-4"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Login Background Yönetimi */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-6 flex items-center">
                <Globe className="w-5 h-5 mr-2 text-primary" />
                {t('sysSettings.loginBackground')}
              </h3>
              
              <div className="space-y-4">
                {/* Login Background Upload */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('sysSettings.loginBgImage')}
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          // Dosya validasyonu
                          const maxSize = 5 * 1024 * 1024; // 5MB
                          const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
                          
                          if (file.size > maxSize) {
                            setError(t('sysSettings.errFileTooLarge5MB'));
                            e.target.value = '';
                            return;
                          }

                          if (!allowedTypes.includes(file.type)) {
                            setError(t('sysSettings.errInvalidBgType'));
                            e.target.value = '';
                            return;
                          }
                          
                          setLoginBackgroundFile(file);
                          setError(null);
                          
                          // Otomatik yükleme
                          setUploadingLoginBackground(true);
                          try {
                            const formData = new FormData();
                            formData.append('login_background', file);
                            const response = await apiService.uploadLoginBackground(formData);
                            
                            // Backend'den dönen path'i kullan - timestamp ile cache bypass
                            const timestamp = new Date().getTime();
                            const backgroundPath = response.path || '/uploads/login-background.jpg';
                            const backgroundUrl = `${backgroundPath}?t=${timestamp}`;
                            setCurrentLoginBackground(backgroundUrl);
                            
                            // System settings'i yeniden yükle
                            await fetchSystemSettings();
                            
                            // Global event dispatch et
                            window.dispatchEvent(new CustomEvent('loginBackgroundUpdated', { 
                              detail: { backgroundPath: backgroundUrl } 
                            }));
                            
                            setError(null);
                            setSuccessMessage(t('sysSettings.loginBgUploaded'));
                            
                            // Başarı mesajını 3 saniye sonra temizle
                            setTimeout(() => {
                              setSuccessMessage(null);
                            }, 3000);
                            
                            // Input'u temizle
                            e.target.value = '';
                            setLoginBackgroundFile(null);
                            
                          } catch (error) {
                            console.error('Login background upload error:', error);
                            let errorMessage = t('sysSettings.loginBgDeleteError');

                            if (error instanceof Error) {
                              if (error.message.includes('413')) {
                                errorMessage = t('sysSettings.errFileTooLarge5MB');
                              } else if (error.message.includes('415')) {
                                errorMessage = t('sysSettings.errInvalidBgType');
                              } else if (error.message.includes('401')) {
                                errorMessage = t('sysSettings.unknownError');
                              } else if (error.message.includes('500')) {
                                errorMessage = t('sysSettings.unknownError');
                              } else {
                                errorMessage = error.message;
                              }
                            }
                            
                            setError(errorMessage);
                            // Hata durumunda da input'u temizle
                            e.target.value = '';
                            setLoginBackgroundFile(null);
                          } finally {
                            setUploadingLoginBackground(false);
                          }
                        }
                      }}
                      className="flex-1 text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary-dark hover:file:bg-primary/20"
                      disabled={uploadingLoginBackground}
                    />
                    {uploadingLoginBackground && (
                      <RefreshCw className="w-4 h-4 animate-spin text-green-600" />
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('sysSettings.loginBgHint')}</p>
                  
                  {/* Mevcut Login Background */}
                  {currentLoginBackground && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('sysSettings.currentLoginBg')}:
                      </label>
                      <div className="flex items-center space-x-3">
                        <img 
                          src={currentLoginBackground}
                          alt="Login Background" 
                          className="w-32 h-20 object-cover rounded-lg border border-gray-200 dark:border-gray-700"
                        />
                        <button
                          onClick={async () => {
                            if (confirm(t('sysSettings.removeLoginBgConfirm'))) {
                              try {
                                await apiService.deleteLoginBackground();
                                setCurrentLoginBackground(null);
                                setSuccessMessage(t('sysSettings.loginBgRemoved'));

                                // Başarı mesajını 3 saniye sonra temizle
                                setTimeout(() => {
                                  setSuccessMessage(null);
                                }, 3000);
                              } catch (error) {
                                console.error('Login background silme hatası:', error);
                                setError(t('sysSettings.loginBgDeleteError'));
                              }
                            }
                          }}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          {t('sysSettings.removeLoginBg')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-3 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('sysSettings.settingsListTitle')}</h3>
              <div className="space-y-4">
                {systemSettings && systemSettings.length > 0 ? systemSettings.map((setting) => (
                  <div
                    key={setting.id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors gap-3"
                  >
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center mb-2 gap-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {setting.setting_key}
                        </span>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full w-fit ${getCategoryColor(setting.category)}`}>
                          {settingCategories.find(c => c.value === setting.category)?.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{setting.description}</p>
                      <div className="flex flex-col sm:flex-row sm:items-center text-xs text-gray-500 dark:text-gray-400 gap-1 sm:gap-4">
                        <span>{t('sysSettings.settingValue')}: {setting.is_encrypted ? '********' : setting.setting_value}</span>
                        <span>{t('sysSettings.settingType')}: {setting.setting_type}</span>
                        <span>{t('sysSettings.settingUpdated')}: {new Date(setting.updated_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}</span>
                      </div>
                    </div>
                    <div className="flex space-x-2 justify-end">
                      <button
                        onClick={() => handleEdit(setting, 'setting')}
                        className="p-2 text-gray-400 hover:text-primary transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(setting.id, 'setting')}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    {t('sysSettings.noSettings')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Backup Tab */}
      {activeTab === 'backup' && (
        <div className="space-y-6">

          {/* Backup Configurations */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('sysSettings.backupPlans')}</h3>
              <div className="space-y-4">
                {backupConfigs && backupConfigs.length > 0 ? backupConfigs.map((backup: any) => (
                  <div
                    key={backup.id}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mr-3">
                            {backup.backup_name}
                          </h4>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getBackupStatusColor(backup.status)}`}>
                            {backup.status === 'active' ? t('sysSettings.backupStatusActive') : backup.status === 'paused' ? t('sysSettings.backupStatusPaused') : t('sysSettings.backupStatusDisabled')}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm text-gray-600 dark:text-gray-400">
                          <div>
                            <span className="font-medium">{t('sysSettings.backupType')}:</span> {backup.backup_type}
                          </div>
                          <div>
                            <span className="font-medium">{t('sysSettings.backupSchedule')}:</span> {backup.schedule_cron}
                          </div>
                          <div>
                            <span className="font-medium">{t('sysSettings.backupRetention')}:</span> {t('sysSettings.backupRetentionDays').replace('{days}', String(backup.retention_days))}
                          </div>
                          <div>
                            <span className="font-medium">{t('sysSettings.backupSize')}:</span> {backup.backup_size_gb} GB
                          </div>
                          <div>
                            <span className="font-medium">{t('sysSettings.backupSuccess')}:</span> {backup.success_rate}%
                          </div>
                          <div>
                            <span className="font-medium">{t('sysSettings.backupLast')}:</span> {backup.last_backup ? new Date(backup.last_backup).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR') : t('sysSettings.backupNever')}
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => runBackup(backup)}
                          className="p-1 text-green-600 hover:text-green-800 transition-colors"
                          title={t('sysSettings.backupRunNow')}
                        >
                          <Play className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(backup, 'backup')}
                          className="p-1 text-primary hover:text-primary-dark transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(backup.id, 'backup')}
                          className="p-1 text-red-600 hover:text-red-800 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    {t('sysSettings.noBackupPlans')}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Recent Backup Logs */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('sysSettings.recentBackupLogs')}</h3>
              <div className="space-y-3">
                {backupLogs && backupLogs.length > 0 ? backupLogs.map((log: any) => (
                  <div
                    key={log.id}
                    className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="flex items-center mb-1">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 mr-3">
                          {log.backup_config?.backup_name}
                        </span>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getLogStatusColor(log.status)}`}>
                          {log.status === 'completed' ? t('sysSettings.logStatusCompleted') :
                           log.status === 'running' ? t('sysSettings.logStatusRunning') :
                           log.status === 'failed' ? t('sysSettings.logStatusFailed') : t('sysSettings.logStatusCancelled')}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        <span className="mr-4">{t('sysSettings.logStart')}: {log.start_time ? new Date(log.start_time).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR') : t('sysSettings.logUnknown')}</span>
                        {log.end_time && <span className="mr-4">{t('sysSettings.logEnd')}: {new Date(log.end_time).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}</span>}
                        {log.duration_minutes && <span className="mr-4">{t('sysSettings.logDuration')}: {t('sysSettings.logDurationMin').replace('{min}', String(log.duration_minutes))}</span>}
                        <span>{t('sysSettings.logSizeGb')}: {log.total_size_gb || 0} GB</span>
                      </div>
                      {log.error_message && (
                        <div className="text-xs text-red-600 mt-1">
                          {t('sysSettings.logError')}: {log.error_message}
                        </div>
                      )}
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    {t('sysSettings.noBackupLogs')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Tab */}
      {activeTab === 'email' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('sysSettings.emailConfigs')}</h2>
            <button
              onClick={() => handleAdd('email')}
              className="w-full sm:w-auto flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors min-h-[44px] text-sm sm:text-base"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('sysSettings.newSmtp')}
            </button>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6">
              <div className="space-y-4">
                {emailConfigs && emailConfigs.length > 0 ? emailConfigs.map((config: any) => (
                  <div
                    key={config.id}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mr-3">
                            {config.config_name}
                          </h4>
                          {config.is_default && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-primary/20 text-primary-dark">
                              {t('sysSettings.emailDefault')}
                            </span>
                          )}
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ml-2 ${
                            config.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {config.is_active ? t('sysSettings.active') : t('sysSettings.passive')}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm text-gray-600 dark:text-gray-400">
                          <div>
                            <span className="font-medium">{t('sysSettings.emailSmtp')}:</span> {config.smtp_host}:{config.smtp_port}
                          </div>
                          <div>
                            <span className="font-medium">{t('sysSettings.emailEncryption')}:</span> {config.smtp_encryption.toUpperCase()}
                          </div>
                          <div>
                            <span className="font-medium">{t('sysSettings.emailFrom')}:</span> {config.from_email}
                          </div>
                          <div>
                            <span className="font-medium">{t('sysSettings.emailDailyLimit')}:</span> {config.daily_limit}
                          </div>
                          <div>
                            <span className="font-medium">{t('sysSettings.emailMonthlyLimit')}:</span> {config.monthly_limit}
                          </div>
                          <div>
                            <span className="font-medium">{t('sysSettings.emailLastTest')}:</span>
                            <span className={config.last_tested ? 'text-green-600' : 'text-gray-500 dark:text-gray-400'}>
                              {config.last_tested ? new Date(config.last_tested).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR') : t('sysSettings.emailNeverTested')}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => testEmailConfig(config)}
                          className="p-1 text-green-600 hover:text-green-800 transition-colors"
                          title={t('sysSettings.emailTestTitle')}
                        >
                          <Play className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(config, 'email')}
                          className="p-1 text-primary hover:text-primary-dark transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(config.id, 'email')}
                          className="p-1 text-red-600 hover:text-red-800 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    {t('sysSettings.noEmailConfigs')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <div className="space-y-6">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('sysSettings.securityTitle')}</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('sysSettings.sessionSettings')}</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t('sysSettings.sessionTimeout')}</span>
                  <span className="text-sm font-medium">{t('sysSettings.sessionTimeoutValue')}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t('sysSettings.maxLoginAttempts')}</span>
                  <span className="text-sm font-medium">5</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t('sysSettings.twoFactor')}</span>
                  <span className="text-sm font-medium text-green-600">{t('sysSettings.active')}</span>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('sysSettings.apiSecurity')}</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t('sysSettings.rateLimit')}</span>
                  <span className="text-sm font-medium">{t('sysSettings.rateLimitValue')}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t('sysSettings.ipRestriction')}</span>
                  <span className="text-sm font-medium text-green-600">{t('sysSettings.active')}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t('sysSettings.apiKeyExpiry')}</span>
                  <span className="text-sm font-medium">{t('sysSettings.apiKeyExpiryValue')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Logs Tab */}
      {activeTab === 'logs' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('sysSettings.systemLogsTitle')}</h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={t('sysSettings.searchLogs')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-64"
              />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6">
              <div className="space-y-3">
                {systemLogs && systemLogs.length > 0 ? systemLogs.filter((log: any) => 
                  (log.message && searchTerm && String(log.message || '').toLowerCase().includes(searchTerm.toLowerCase())) ||
                  (log.category && searchTerm && String(log.category || '').toLowerCase().includes(searchTerm.toLowerCase()))
                ).map((log: any) => (
                  <div
                    key={log.id}
                    className="flex items-start space-x-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
                  >
                    <div className="flex-shrink-0">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getLogLevelColor(log.log_level)}`}>
                        {log.log_level.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {log.message}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {log.created_at ? new Date(log.created_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR') : t('sysSettings.logUnknown')}
                        </span>
                      </div>
                      {log.details && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{log.details}</p>
                      )}
                      <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                        <span className="mr-4">{t('sysSettings.logCategory')}: {log.category}</span>
                        {log.user && <span className="mr-4">{t('sysSettings.logUser')}: {log.user.name}</span>}
                        {log.ip_address && <span>IP: {log.ip_address}</span>}
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    {t('sysSettings.noSystemLogs')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Login Logs Tab */}
      {activeTab === 'login-logs' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('sysSettings.loginLogsTitle')}</h2>
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  const csvContent = [
                    [t('sysSettings.logStart'), 'E-posta', t('sysSettings.statusSuccess'), 'IP', t('sysSettings.logError')],
                    ...loginLogs.map(log => [
                      new Date(log.created_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR'),
                      log.email,
                      log.success ? t('sysSettings.csvSuccess') : log.blocked_until ? t('sysSettings.csvBlocked') : t('sysSettings.csvFailed'),
                      log.ip_address || '',
                      log.failure_reason || ''
                    ])
                  ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
                  
                  const BOM = '\uFEFF';
                  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
                  const link = document.createElement('a');
                  link.href = URL.createObjectURL(blob);
                  link.download = `giris-loglari-${new Date().toISOString().split('T')[0]}.csv`;
                  link.click();
                }}
                className="flex items-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors min-h-[44px] text-sm sm:text-base"
              >
                <Download className="w-4 h-4 mr-2" />
                {t('sysSettings.exportExcel')}
              </button>
              <button
                onClick={async () => {
                  if (confirm(t('sysSettings.clearLogsConfirm'))) {
                    try {
                      await apiService.clearLoginLogs();
                      await fetchLoginLogs();
                    } catch (error) {
                      console.error('Clear logs error:', error);
                      setError(error instanceof Error ? error.message : t('sysSettings.clearLogs'));
                    }
                  }
                }}
                className="flex items-center px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors min-h-[44px] text-sm sm:text-base"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t('sysSettings.clearLogs')}
              </button>
              <button
                onClick={fetchLoginLogs}
                className="flex items-center px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors min-h-[44px] text-sm sm:text-base"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {t('sysSettings.refresh')}
              </button>
            </div>
          </div>

          {/* Blocked Accounts */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('sysSettings.blockedAccounts')}</h3>
              {blockedAccounts.length > 0 ? (
                <div className="space-y-3">
                  {blockedAccounts.map((account, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 border border-red-200 rounded-lg bg-red-50"
                    >
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100 mr-3">
                            {account.email}
                          </span>
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                            {t('sysSettings.blocked')}
                          </span>
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          <span className="mr-4">{t('sysSettings.failedAttempts')}: {account.failed_attempts || 0}</span>
                          <span className="mr-4">{t('sysSettings.blockedUntil')}: {account.blocked_until ? new Date(account.blocked_until).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR') : t('sysSettings.logUnknown')}</span>
                          <span>{t('sysSettings.lastAttempt')}: {account.last_attempt ? new Date(account.last_attempt).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR') : t('sysSettings.logUnknown')}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleUnblockAccount(account.email)}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                      >
                        {t('sysSettings.unblock')}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  {t('sysSettings.noBlockedAccounts')}
                </div>
              )}
            </div>
          </div>

          {/* Login Logs */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('sysSettings.loginLogsSubtitle')}</h3>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {t('sysSettings.totalRecords').replace('{count}', String(loginLogs.length))} - {t('sysSettings.showing110').replace('{count}', '10')}
                </div>
              </div>
              <div className="space-y-3">
                {loginLogs && loginLogs.length > 0 ? loginLogs.slice(0, 10).map((log) => (
                  <div
                    key={log.id}
                    className={`flex items-start space-x-3 p-3 border rounded-lg ${
                      log.success 
                        ? 'border-green-200 bg-green-50' 
                        : log.blocked_until 
                        ? 'border-red-200 bg-red-50'
                        : 'border-yellow-200 bg-yellow-50'
                    }`}
                  >
                    <div className="flex-shrink-0">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        log.success 
                          ? 'bg-green-100 text-green-800' 
                          : log.blocked_until 
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {log.success ? t('sysSettings.statusSuccess') : log.blocked_until ? t('sysSettings.statusBlocked') : t('sysSettings.statusFailed')}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {log.email}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {log.created_at ? new Date(log.created_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR') : t('sysSettings.logUnknown')}
                        </span>
                      </div>
                      {log.failure_reason && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{log.failure_reason}</p>
                      )}
                      <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                        {log.ip_address && <span className="mr-4">IP: {log.ip_address}</span>}
                        {log.blocked_until && (
                          <span className="text-red-600">{t('sysSettings.engagedUntil')}: {new Date(log.blocked_until).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}</span>
                        )}
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    {t('sysSettings.noLoginLogs')}
                  </div>
                )}
              </div>
              
              {/* Pagination */}
              {loginLogs.length > 10 && (
                <div className="mt-6 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t('sysSettings.showing110').replace('{count}', String(loginLogs.length))}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
                      disabled
                    >
                      {t('sysSettings.prev')}
                    </button>
                    <button
                      onClick={() => {
                        // Tüm kayıtları göster
                        const allLogs = document.querySelector('.space-y-3');
                        if (allLogs) {
                          allLogs.innerHTML = '';
                          loginLogs.forEach((log, index) => {
                            const logElement = document.createElement('div');
                            logElement.className = `flex items-start space-x-3 p-3 border rounded-lg ${
                              log.success 
                                ? 'border-green-200 bg-green-50' 
                                : log.blocked_until 
                                ? 'border-red-200 bg-red-50'
                                : 'border-yellow-200 bg-yellow-50'
                            }`;
                            logElement.innerHTML = `
                              <div class="flex-shrink-0">
                                <span class="inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  log.success 
                                    ? 'bg-green-100 text-green-800' 
                                    : log.blocked_until 
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }">
                                  ${log.success ? t('sysSettings.statusSuccess') : log.blocked_until ? t('sysSettings.statusBlocked') : t('sysSettings.statusFailed')}
                                </span>
                              </div>
                              <div class="flex-1 min-w-0">
                                <div class="flex items-center justify-between mb-1">
                                  <span class="text-sm font-medium text-gray-900 dark:text-gray-100">${log.email}</span>
                                  <span class="text-xs text-gray-500 dark:text-gray-400">${new Date(log.created_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}</span>
                                </div>
                                ${log.failure_reason ? `<p class="text-sm text-gray-600 dark:text-gray-400 mb-1">${log.failure_reason}</p>` : ''}
                                <div class="flex items-center text-xs text-gray-500 dark:text-gray-400">
                                  ${log.ip_address ? `<span class="mr-4">IP: ${log.ip_address}</span>` : ''}
                                  ${log.blocked_until ? `<span class="text-red-600">${t('sysSettings.engagedUntil')}: ${new Date(log.blocked_until).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}</span>` : ''}
                                </div>
                              </div>
                            `;
                            allLogs.appendChild(logElement);
                          });
                        }
                      }}
                      className="px-3 py-1 text-sm bg-primary text-white rounded hover:bg-primary-dark transition-colors"
                    >
                      {t('sysSettings.showAll')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cron Jobs Tab */}
      {activeTab === 'cron' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('sysSettings.cronTitle')}</h2>
            <button
              onClick={() => handleAdd('cron')}
              className="w-full sm:w-auto flex items-center justify-center px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors min-h-[44px] text-sm sm:text-base"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('sysSettings.newCronJob')}
            </button>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6">
              <div className="space-y-4">
                {cronJobs && cronJobs.length > 0 ? cronJobs.map((job: any) => (
                  <div
                    key={job.id}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mr-3">
                            {job.job_name}
                          </h4>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            job.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {job.is_active ? t('sysSettings.active') : t('sysSettings.passive')}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm text-gray-600 dark:text-gray-400 mb-3">
                          <div>
                            <span className="font-medium">{t('sysSettings.cronExpression')}:</span> {job.cron_expression}
                          </div>
                          <div>
                            <span className="font-medium">{t('sysSettings.cronRunCount')}:</span> {t('sysSettings.cronRunCountValue').replace('{count}', String(job.run_count))}
                          </div>
                          <div>
                            <span className="font-medium">{t('sysSettings.cronSuccess')}:</span> {job.success_count}/{job.run_count}
                          </div>
                          <div className="col-span-1 sm:col-span-2 lg:col-span-3">
                            <span className="font-medium">{t('sysSettings.cronCommand')}:</span>
                            <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded ml-2">{job.command}</code>
                          </div>
                          {job.description && (
                            <div className="col-span-1 sm:col-span-2 lg:col-span-3">
                              <span className="font-medium">{t('sysSettings.cronDescription')}:</span> {job.description}
                            </div>
                          )}
                          <div>
                            <span className="font-medium">{t('sysSettings.cronLastRun')}:</span> {job.last_run ? new Date(job.last_run).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR') : t('sysSettings.cronNever')}
                          </div>
                          <div>
                            <span className="font-medium">{t('sysSettings.cronNextRun')}:</span> {job.next_run ? new Date(job.next_run).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR') : t('sysSettings.cronUnknown')}
                          </div>
                          <div>
                            <span className="font-medium">{t('sysSettings.cronCreatedBy')}:</span> {job.created_by_name || t('sysSettings.cronSystem')}
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={async () => {
                            try {
                              await apiService.toggleCronJob(job.id);
                              await fetchCronJobs();
                            } catch (error) {
                              setError(t('sysSettings.cronToggleError'));
                            }
                          }}
                          className={`p-1 transition-colors ${
                            job.is_active 
                              ? 'text-yellow-600 hover:text-yellow-800' 
                              : 'text-green-600 hover:text-green-800'
                          }`}
                          title={job.is_active ? t('sysSettings.cronPause') : t('sysSettings.cronActivate')}
                        >
                          {job.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              const result = await apiService.runCronJob(job.id);
                              alert(`✅ ${t('sysSettings.cronRunNow')}!\n\n${result.duration?.toFixed(2)}s`);
                              await fetchCronJobs();
                            } catch (error) {
                              alert(`❌ ${t('sysSettings.cronDeleteError')}: ${error instanceof Error ? error.message : t('sysSettings.unknownError')}`);
                            }
                          }}
                          className="p-1 text-green-600 hover:text-green-800 transition-colors"
                          title={t('sysSettings.cronRunNow')}
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setEditingItem(job);
                            setModalType('cron');
                            setCronData({
                              job_name: job.job_name,
                              cron_expression: job.cron_expression,
                              command: job.command,
                              script: job.script || '',
                              description: job.description || '',
                              is_active: job.is_active,
                              notification_recipients: job.notification_recipients || []
                            });
                            setShowModal(true);
                          }}
                          className="p-1 text-primary hover:text-primary-dark transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={async () => {
                            if (confirm(t('sysSettings.cronDeleteConfirm'))) {
                              try {
                                await apiService.deleteCronJob(job.id);
                                await fetchCronJobs();
                              } catch (error) {
                                setError(t('sysSettings.cronDeleteError'));
                              }
                            }
                          }}
                          className="p-1 text-red-600 hover:text-red-800 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    {t('sysSettings.noCronJobs')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Orders Settings Tab */}
      {activeTab === 'orders' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('sysSettings.ordersTitle')}</h2>
          </div>

          <div className="bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <h3 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">{t('sysSettings.capexTitle')}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {t('sysSettings.capexDesc')}
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('sysSettings.capexMinAmount')}
                </label>
                <div className="flex items-center space-x-3">
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={orderCapexLimit}
                    onChange={(e) => setOrderCapexLimit(parseFloat(e.target.value) || 0)}
                    className="w-full max-w-xs px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent min-h-[44px]"
                    placeholder="200"
                  />
                  <button
                    onClick={async () => {
                      try {
                        await apiService.upsertSystemSetting('order_capex_limit', {
                          setting_value: orderCapexLimit.toString(),
                          setting_type: 'string',
                          category: 'orders',
                          description: 'Sipariş oluştururken CAPEX seçiminin zorunlu olduğu minimum tutar (USD)',
                          is_encrypted: false
                        });
                        await fetchSystemSettings();
                        alert(t('sysSettings.capexSaved'));
                      } catch (error) {
                        console.error('Error updating capex limit:', error);
                        alert(t('sysSettings.capexSaveError'));
                      }
                    }}
                    className="px-4 py-3 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors flex items-center min-h-[44px] text-sm sm:text-base whitespace-nowrap"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {t('sysSettings.save')}
                  </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  {t('sysSettings.capexHint')}
                </p>
              </div>

              <div className="bg-primary/10 border border-primary/30 rounded-md p-4">
                <div className="flex">
                  <AlertTriangle className="w-5 h-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-primary-dark">
                    <p className="font-medium mb-1">{t('sysSettings.capexNotes')}:</p>
                    <ul className="list-disc ml-4 space-y-1">
                      <li>{t('sysSettings.capexNote1')}</li>
                      <li>{t('sysSettings.capexNote2')}</li>
                      <li>{t('sysSettings.capexNote3')}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recovery Tab */}
      {activeTab === 'recovery' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('sysSettings.recoveryTitle')}</h2>
          </div>

          {/* Source Selection */}
          <div className="bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <h3 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">{t('sysSettings.selectBackupSource')}</h3>
            <div className="flex space-x-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="backupSource"
                  value="upload"
                  checked={backupSource === 'upload'}
                  onChange={(e) => setBackupSource(e.target.value as 'upload' | 'server')}
                  className="w-4 h-4 text-primary bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-primary"
                />
                <Upload className="w-5 h-5 ml-2 mr-2 text-gray-600 dark:text-gray-400" />
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('sysSettings.uploadFile')}</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="backupSource"
                  value="server"
                  checked={backupSource === 'server'}
                  onChange={(e) => setBackupSource(e.target.value as 'upload' | 'server')}
                  className="w-4 h-4 text-primary bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-primary"
                />
                <Server className="w-5 h-5 ml-2 mr-2 text-gray-600 dark:text-gray-400" />
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('sysSettings.serverBackup')}</span>
              </label>
            </div>
          </div>

          {/* File Upload Section */}
          {backupSource === 'upload' && (
            <div className="bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h3 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">{t('sysSettings.step1Upload')}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('sysSettings.backupFileLabel')}
                </label>
                <input
                  type="file"
                  accept=".gz,.sql.gz"
                  onChange={handleFileUpload}
                  className="block w-full text-sm text-gray-500 dark:text-gray-400
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-full file:border-0
                    file:text-sm file:font-semibold
                    file:bg-primary/10 file:text-primary-dark
                    hover:file:bg-primary/20"
                />
              </div>
              {recoveryFile && (
                <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                    <span className="text-sm text-green-700">
                      {recoveryFile.name} ({(recoveryFile.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                  </div>
                  <button
                    onClick={analyzeBackupFile}
                    disabled={loading || isRecovering}
                    className="px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 min-h-[44px] text-sm sm:text-base"
                  >
                    {loading ? t('sysSettings.analyzing') : t('sysSettings.analyze')}
                  </button>
                </div>
              )}
            </div>
          </div>
          )}

          {/* Server Backup Selection */}
          {backupSource === 'server' && (
            <div className="bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h3 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">{t('sysSettings.step1ServerBackup')}</h3>
              <div className="space-y-4">
                {serverBackups.length > 0 ? (
                  <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                    {serverBackups.map((backup, index) => (
                      <div key={backup.filename} className={`p-3 border-b border-gray-100 ${index % 2 === 0 ? 'bg-gray-50 dark:bg-gray-700' : 'bg-white dark:bg-gray-800'}`}>
                        <label className="flex items-center justify-between cursor-pointer">
                          <div className="flex items-center">
                            <input
                              type="radio"
                              name="serverBackup"
                              value={backup.filename}
                              checked={selectedServerBackup === backup.filename}
                              onChange={(e) => handleServerBackupSelect(e.target.value)}
                              className="w-4 h-4 text-primary bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-primary"
                            />
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{backup.filename}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {backup.backupDate ? new Date(backup.backupDate).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR') : t('sysSettings.logUnknown')} • {backup.sizeFormatted || t('sysSettings.logUnknown')}
                              </div>
                            </div>
                          </div>
                          <div className="text-xs text-gray-400">
                            {backup.backupDate ? ((Date.now() - new Date(backup.backupDate).getTime()) / (1000 * 60 * 60)).toFixed(0) + ' ' + t('sysSettings.hoursAgo').replace('{hours}', '') : t('sysSettings.logUnknown')}
                          </div>
                        </label>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <Database className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>{t('sysSettings.noServerBackups')}</p>
                  </div>
                )}
                
                {selectedServerBackup && (
                  <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                      <span className="text-sm text-green-700">
                        {selectedServerBackup} {t('sysSettings.selected')}
                      </span>
                    </div>
                    <button
                      onClick={analyzeServerBackup}
                      disabled={loading || isRecovering}
                      className="px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 min-h-[44px] text-sm sm:text-base"
                    >
                      {loading ? t('sysSettings.analyzing') : t('sysSettings.analyze')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Analysis Results */}
          {backupAnalysis && (
            <div className="bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h3 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">{t('sysSettings.step2Tables')}</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {t('sysSettings.tablesFound').replace('{count}', String(backupAnalysis.tables?.length || 0))}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedTables(backupAnalysis.tables?.map((t: any) => t.name) || [])}
                      className="text-sm text-primary hover:text-primary-dark"
                    >
                      {t('sysSettings.selectAll')}
                    </button>
                    <button
                      onClick={() => setSelectedTables([])}
                      className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:text-gray-200"
                    >
                      {t('sysSettings.deselectAll')}
                    </button>
                  </div>
                </div>
                <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                  {backupAnalysis.tables?.map((table: any, index: number) => (
                    <div key={table.name} className={`p-3 border-b border-gray-100 ${index % 2 === 0 ? 'bg-gray-50 dark:bg-gray-700' : 'bg-white dark:bg-gray-800'}`}>
                      <div className="flex items-center justify-between">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedTables.includes(table.name)}
                            onChange={(e) => handleTableSelection(table.name, e.target.checked)}
                            className="w-4 h-4 text-primary bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-primary"
                          />
                          <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-100">{table.name}</span>
                        </label>
                        <div className="flex items-center space-x-4">
                          {/* Record Count Comparison */}
                          {typeof table.currentRecords !== 'undefined' && (
                            <div className="flex items-center space-x-2 text-xs">
                              <div className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                                {t('sysSettings.currentRecords')}: {table.currentRecords}
                              </div>
                              <div className="bg-primary/20 px-2 py-1 rounded">
                                {t('sysSettings.backupRecords')}: {table.backupRecords || table.recordCount || 0}
                              </div>
                              {table.recordDiff && (
                                <div className={`px-2 py-1 rounded font-medium ${
                                  table.recordDiff > 0 
                                    ? 'bg-green-100 text-green-800' 
                                    : table.recordDiff < 0 
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                                }`}>
                                  {table.recordDiff > 0 ? '+' : ''}{table.recordDiff}
                                </div>
                              )}
                            </div>
                          )}
                          {typeof table.currentRecords === 'undefined' && (
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {table.recordCount || 0} {t('sysSettings.backupRecords')}
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Table Status Indicators */}
                      {typeof table.currentRecords !== 'undefined' && (
                        <div className="mt-2 flex items-center space-x-2">
                          {table.tableExists === false && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              {t('sysSettings.tableNotExists')}
                            </span>
                          )}
                          {table.recordDiff > 0 && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              ↑ {t('sysSettings.backupHasMore')}
                            </span>
                          )}
                          {table.recordDiff < 0 && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              ↓ {t('sysSettings.currentHasMore')}
                            </span>
                          )}
                          {table.recordDiff === 0 && table.currentRecords > 0 && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                              = {t('sysSettings.dataEqual')}
                            </span>
                          )}
                        </div>
                      )}
                      {table.structure && (
                        <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                          {table.structure.substring(0, 100)}...
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Recovery Controls */}
          {backupAnalysis && selectedTables.length > 0 && (
            <div className="bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h3 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">{t('sysSettings.step3Recovery')}</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center">
                    <AlertTriangle className="w-5 h-5 text-yellow-500 mr-2" />
                    <span className="text-sm text-yellow-700">
                      {t('sysSettings.tablesSelected').replace('{count}', String(selectedTables.length))}
                    </span>
                  </div>
                  <button
                    onClick={startRecovery}
                    disabled={isRecovering}
                    className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    {isRecovering ? t('sysSettings.restoring') : t('sysSettings.restore')}
                  </button>
                </div>

                {isRecovering && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span>{t('sysSettings.progress')}</span>
                      <span>{recoveryProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all duration-300" 
                        style={{ width: `${recoveryProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Recovery Logs */}
          {recoveryLogs.length > 0 && (
            <div className="bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h3 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">{t('sysSettings.operationLogs')}</h3>
              <div className="max-h-48 overflow-y-auto bg-gray-900 text-green-400 text-sm font-mono p-4 rounded-lg">
                {recoveryLogs.map((log, index) => (
                  <div key={index} className="mb-1">
                    [{new Date().toLocaleTimeString()}] {log}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bulk Print Tab */}
      {activeTab === 'bulk-print' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('sysSettings.bulkPrintTitle')}</h2>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('sysSettings.printSettings')}</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('sysSettings.locationSelect')} *
                      </label>
                      <select
                        value={bulkPrintData.location_id}
                        onChange={(e) => setBulkPrintData({...bulkPrintData, location_id: parseInt(e.target.value)})}
                        className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px]"
                      >
                        <option value={0}>{t('sysSettings.selectLocation')}</option>
                        {locationsData.map((location) => (
                          <option key={location.id} value={location.id}>
                            {location.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('sysSettings.labelType')} *
                      </label>
                      <div className="flex space-x-4">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="cabinet"
                            checked={bulkPrintData.print_type === 'cabinet'}
                            onChange={(e) => setBulkPrintData({...bulkPrintData, print_type: e.target.value})}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{t('sysSettings.cabinetLabel')}</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="switch"
                            checked={bulkPrintData.print_type === 'switch'}
                            onChange={(e) => setBulkPrintData({...bulkPrintData, print_type: e.target.value})}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{t('sysSettings.switchLabel')}</span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('sysSettings.labelDimensions')}
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                            {t('sysSettings.labelWidth')}
                          </label>
                          <input
                            type="number"
                            min="50"
                            max="200"
                            value={bulkPrintData.label_width}
                            onChange={(e) => setBulkPrintData({...bulkPrintData, label_width: parseInt(e.target.value) || 100})}
                            className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px]"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                            {t('sysSettings.labelHeight')}
                          </label>
                          <input
                            type="number"
                            min="30"
                            max="100"
                            value={bulkPrintData.label_height}
                            onChange={(e) => setBulkPrintData({...bulkPrintData, label_height: parseInt(e.target.value) || 50})}
                            className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px]"
                          />
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3">
                        <button
                          onClick={() => setBulkPrintData({...bulkPrintData, label_width: 100, label_height: 50})}
                          className="px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 dark:bg-gray-600 transition-colors"
                        >
                          100x50mm
                        </button>
                        <button
                          onClick={() => setBulkPrintData({...bulkPrintData, label_width: 90, label_height: 50})}
                          className="px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 dark:bg-gray-600 transition-colors"
                        >
                          90x50mm
                        </button>
                        <button
                          onClick={() => setBulkPrintData({...bulkPrintData, label_width: 80, label_height: 40})}
                          className="px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 dark:bg-gray-600 transition-colors"
                        >
                          80x40mm
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('sysSettings.labelOrientation')}
                      </label>
                      <div className="flex space-x-4">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="horizontal"
                            checked={bulkPrintData.orientation === 'horizontal'}
                            onChange={(e) => setBulkPrintData({...bulkPrintData, orientation: e.target.value})}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{t('sysSettings.horizontal')}</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="vertical"
                            checked={bulkPrintData.orientation === 'vertical'}
                            onChange={(e) => setBulkPrintData({...bulkPrintData, orientation: e.target.value})}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{t('sysSettings.vertical')}</span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('sysSettings.margins')}
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                            {t('sysSettings.marginTop')}
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="10"
                            value={bulkPrintData.margin_top}
                            onChange={(e) => setBulkPrintData({...bulkPrintData, margin_top: parseInt(e.target.value) || 2})}
                            className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px]"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                            {t('sysSettings.marginRight')}
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="10"
                            value={bulkPrintData.margin_right}
                            onChange={(e) => setBulkPrintData({...bulkPrintData, margin_right: parseInt(e.target.value) || 2})}
                            className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px]"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                            {t('sysSettings.marginBottom')}
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="10"
                            value={bulkPrintData.margin_bottom}
                            onChange={(e) => setBulkPrintData({...bulkPrintData, margin_bottom: parseInt(e.target.value) || 2})}
                            className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px]"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                            {t('sysSettings.marginLeft')}
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="10"
                            value={bulkPrintData.margin_left}
                            onChange={(e) => setBulkPrintData({...bulkPrintData, margin_left: parseInt(e.target.value) || 2})}
                            className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px]"
                          />
                        </div>
                      </div>
                    </div>

                    {bulkPrintData.print_type === 'cabinet' && (
                      <div>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={bulkPrintData.include_switches}
                            onChange={(e) => setBulkPrintData({...bulkPrintData, include_switches: e.target.checked})}
                            className="rounded border-gray-300 dark:border-gray-600 text-purple-600 shadow-sm focus:border-purple-300 focus:ring focus:ring-purple-200 focus:ring-opacity-50"
                          />
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{t('sysSettings.includeSwitches')}</span>
                        </label>
                      </div>
                    )}

                    <button
                      onClick={printBulkLabels}
                      disabled={bulkPrintItems.length === 0}
                      className="w-full px-4 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      <Printer className="w-4 h-4 mr-2" />
                      {t('sysSettings.printLabels').replace('{count}', String(bulkPrintItems.length))}
                    </button>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                    {t('sysSettings.itemsToPrint').replace('{count}', String(bulkPrintItems.length))}
                  </h3>
                  <div className="max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                    {bulkPrintItems.length > 0 ? (
                      <div className="p-4 space-y-2">
                        {bulkPrintItems.map((item: any) => (
                          <div
                            key={item.id}
                            className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                          >
                            <div>
                              <div className="font-medium text-gray-900 dark:text-gray-100">
                                {bulkPrintData.print_type === 'cabinet' ? item.cabinet_name : item.switch_name}
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-400">
                                {bulkPrintData.print_type === 'cabinet'
                                  ? `${item.company_name || t('sysSettings.noCompany')} - ${item.location_name || t('sysSettings.noLocation')}`
                                  : `${item.brand} ${item.model} - ${item.ip_address}`
                                }
                              </div>
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {bulkPrintData.print_type === 'cabinet' && item.switch_count > 0 && (
                                <span>{item.switch_count} Switch</span>
                              )}
                              {bulkPrintData.print_type === 'switch' && (
                                <span>{item.port_count} Port</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                        {bulkPrintData.location_id === 0
                          ? t('sysSettings.selectLocationFirst')
                          : t('sysSettings.noItemsInLocation').replace('{type}', bulkPrintData.print_type === 'cabinet' ? t('sysSettings.cabinetType') : t('sysSettings.switchType'))
                        }
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* API Tab */}
      {activeTab === 'api' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('sysSettings.apiKeysTitle')}</h2>
            <button
              onClick={() => handleAdd('api')}
              className="w-full sm:w-auto flex items-center justify-center px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors min-h-[44px] text-sm sm:text-base"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('sysSettings.newApiKey')}
            </button>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6">
              <div className="space-y-4">
                {apiKeys && apiKeys.length > 0 ? apiKeys.map((apiKey: any) => (
                  <div
                    key={apiKey.id}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mr-3">
                            {apiKey.key_name}
                          </h4>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            apiKey.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {apiKey.is_active ? t('sysSettings.active') : t('sysSettings.passive')}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-400 mb-3">
                          <div className="col-span-1 sm:col-span-2">
                            <span className="font-medium">API Key:</span>
                            <div className="flex items-center mt-1">
                              <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded mr-2">
                                {showApiKeys[apiKey.id] ? apiKey.api_key : '••••••••••••••••'}
                              </code>
                              <button
                                onClick={() => toggleApiKey(apiKey.id)}
                                className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                              >
                                {showApiKeys[apiKey.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                              </button>
                              <button
                                onClick={() => navigator.clipboard.writeText(apiKey.api_key)}
                                className="text-gray-400 hover:text-gray-600 dark:text-gray-400 ml-2"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                          <div>
                            <span className="font-medium">{t('sysSettings.apiRateLimit')}</span> {apiKey.rate_limit_per_hour}{language === 'en' ? '/hr' : '/saat'}
                          </div>
                          <div>
                            <span className="font-medium">{t('sysSettings.apiUsage')}</span> {(apiKey.usage_count || 0).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}
                          </div>
                          <div>
                            <span className="font-medium">{t('sysSettings.apiLastUsed')}</span> {apiKey.last_used ? new Date(apiKey.last_used).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR') : t('sysSettings.apiNeverUsed')}
                          </div>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          <span className="mr-4">{t('sysSettings.apiPermissions')} {(typeof apiKey.permissions === 'string' ? JSON.parse(apiKey.permissions) : apiKey.permissions || []).join(', ')}</span>
                          {apiKey.expires_at && <span>{t('sysSettings.apiExpiry')} {new Date(apiKey.expires_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}</span>}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(apiKey, 'api')}
                          className="p-1 text-primary hover:text-primary-dark transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(apiKey.id, 'API anahtarı')}
                          className="p-1 text-red-600 hover:text-red-800 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <Key className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>{t('sysSettings.noApiKeys')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[98vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {editingItem ? (
                    modalType === 'setting' ? t('sysSettings.modalEditSetting') :
                    modalType === 'backup' ? t('sysSettings.modalEditBackup') :
                    modalType === 'email' ? t('sysSettings.modalEditEmail') :
                    modalType === 'cron' ? t('sysSettings.modalEditCron') : t('sysSettings.modalEditApi')
                  ) : (
                    modalType === 'setting' ? t('sysSettings.modalNewSetting') :
                    modalType === 'backup' ? t('sysSettings.modalNewBackup') :
                    modalType === 'email' ? t('sysSettings.modalNewEmail') :
                    modalType === 'cron' ? t('sysSettings.modalNewCron') : t('sysSettings.modalNewApi')
                  )}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400 p-1"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-4 sm:p-6 space-y-4">
              {modalType === 'setting' && (
                <>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('sysSettings.settingKey')}
                      </label>
                      <input
                        type="text"
                        value={settingData.setting_key}
                        onChange={(e) => setSettingData({...settingData, setting_key: e.target.value})}
                        className="w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="company_name"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('sysSettings.settingCategory')}
                        </label>
                        <select
                          value={settingData.category}
                          onChange={(e) => setSettingData({...settingData, category: e.target.value as any})}
                          className="w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        >
                          {settingCategories.map((category) => (
                            <option key={category.value} value={category.value}>
                              {category.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('sysSettings.settingDataType')}
                        </label>
                        <select
                          value={settingData.setting_type}
                          onChange={(e) => setSettingData({...settingData, setting_type: e.target.value as any})}
                          className="w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        >
                          <option value="string">String</option>
                          <option value="number">Number</option>
                          <option value="boolean">Boolean</option>
                          <option value="json">JSON</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('sysSettings.settingValue')}
                      </label>
                      <input
                        type={settingData.setting_type === 'number' ? 'number' : 'text'}
                        value={settingData.setting_value}
                        onChange={(e) => setSettingData({...settingData, setting_value: e.target.value})}
                        className="w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder={t('sysSettings.settingValuePlaceholder')}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('sysSettings.settingDesc')}
                    </label>
                    <textarea
                      value={settingData.description}
                      onChange={(e) => setSettingData({...settingData, description: e.target.value})}
                      rows={3}
                      className="w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                      placeholder={t('sysSettings.settingDescPlaceholder')}
                    />
                  </div>

                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settingData.is_encrypted}
                        onChange={(e) => setSettingData({...settingData, is_encrypted: e.target.checked})}
                        className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{t('sysSettings.settingEncrypted')}</span>
                    </label>
                  </div>
                </>
              )}

              {modalType === 'backup' && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('sysSettings.backupName')}
                      </label>
                      <input
                        type="text"
                        value={backupData.backup_name}
                        onChange={(e) => setBackupData({...backupData, backup_name: e.target.value})}
                        className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px]"
                        placeholder={t('sysSettings.backupNamePlaceholder')}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('sysSettings.backupTypeSel')}
                      </label>
                      <select
                        value={backupData.backup_type}
                        onChange={(e) => setBackupData({...backupData, backup_type: e.target.value as any})}
                        className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px]"
                      >
                        <option value="database">{t('sysSettings.backupTypeDatabase')}</option>
                        <option value="files">{t('sysSettings.backupTypeFiles')}</option>
                        <option value="system">{t('sysSettings.backupTypeSystem')}</option>
                        <option value="full">{t('sysSettings.backupTypeFull')}</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('sysSettings.backupSourcePath')}
                      </label>
                      <input
                        type="text"
                        value={backupData.source_path}
                        onChange={(e) => setBackupData({...backupData, source_path: e.target.value})}
                        className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px]"
                        placeholder="/var/lib/postgresql/data"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('sysSettings.backupDestPath')}
                      </label>
                      <input
                        type="text"
                        value={backupData.destination_path}
                        onChange={(e) => setBackupData({...backupData, destination_path: e.target.value})}
                        className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px]"
                        placeholder="/backup/database"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('sysSettings.backupCronSchedule')}
                      </label>
                      <input
                        type="text"
                        value={backupData.schedule_cron}
                        onChange={(e) => setBackupData({...backupData, schedule_cron: e.target.value})}
                        className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px]"
                        placeholder="0 2 * * *"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('sysSettings.backupRetentionDaysSel')}
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={backupData.retention_days}
                        onChange={(e) => setBackupData({...backupData, retention_days: parseInt(e.target.value) || 30})}
                        className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('sysSettings.backupNotifEmails')}
                    </label>
                    {backupData.notification_emails.map((email, index) => (
                      <div key={index} className="flex items-center space-x-2 mb-2">
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => updateEmailInList(index, e.target.value, backupData.notification_emails, (list) => setBackupData({...backupData, notification_emails: list}))}
                          className="flex-1 px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px]"
                          placeholder="admin@company.com"
                        />
                        {backupData.notification_emails.length > 1 && (
                          <button
                            onClick={() => removeEmailFromList(index, backupData.notification_emails, (list) => setBackupData({...backupData, notification_emails: list}))}
                            className="p-2 text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      onClick={() => addEmailToList(backupData.notification_emails, (list) => setBackupData({...backupData, notification_emails: list}))}
                      className="text-sm text-purple-600 hover:text-purple-800"
                    >
                      {t('sysSettings.backupAddEmail')}
                    </button>
                  </div>

                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={backupData.compression_enabled}
                        onChange={(e) => setBackupData({...backupData, compression_enabled: e.target.checked})}
                        className="rounded border-gray-300 dark:border-gray-600 text-purple-600 shadow-sm focus:border-purple-300 focus:ring focus:ring-purple-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{t('sysSettings.backupCompression')}</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={backupData.encryption_enabled}
                        onChange={(e) => setBackupData({...backupData, encryption_enabled: e.target.checked})}
                        className="rounded border-gray-300 dark:border-gray-600 text-purple-600 shadow-sm focus:border-purple-300 focus:ring focus:ring-purple-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{t('sysSettings.settingEncrypted')}</span>
                    </label>
                  </div>
                </>
              )}

              {modalType === 'email' && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('sysSettings.modalNewEmail')} *
                      </label>
                      <input
                        type="text"
                        value={emailData.config_name}
                        onChange={(e) => setEmailData({...emailData, config_name: e.target.value})}
                        className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent min-h-[44px]"
                        placeholder="SMTP"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        SMTP Host *
                      </label>
                      <input
                        type="text"
                        value={emailData.smtp_host}
                        onChange={(e) => setEmailData({...emailData, smtp_host: e.target.value})}
                        className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent min-h-[44px]"
                        placeholder="smtp.company.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        SMTP Port *
                      </label>
                      <input
                        type="number"
                        value={emailData.smtp_port}
                        onChange={(e) => setEmailData({...emailData, smtp_port: parseInt(e.target.value) || 587})}
                        className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent min-h-[44px]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('sysSettings.emailEncryption')} *
                      </label>
                      <select
                        value={emailData.smtp_encryption}
                        onChange={(e) => setEmailData({...emailData, smtp_encryption: e.target.value as any})}
                        className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent min-h-[44px]"
                      >
                        <option value="none">{t('sysSettings.passive')}</option>
                        <option value="tls">TLS</option>
                        <option value="ssl">SSL</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        SMTP Username *
                      </label>
                      <input
                        type="text"
                        value={emailData.smtp_username}
                        onChange={(e) => setEmailData({...emailData, smtp_username: e.target.value})}
                        className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent min-h-[44px]"
                        placeholder="noreply@company.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('sysSettings.emailPassword')}
                      </label>
                      <div className="relative">
                        <input
                          type={showPasswords[0] ? 'text' : 'password'}
                          value={emailData.smtp_password}
                          onChange={(e) => setEmailData({...emailData, smtp_password: e.target.value})}
                          className="w-full px-3 py-3 pr-10 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent min-h-[44px]"
                          placeholder={t('sysSettings.emailPasswordPlaceholder')}
                        />
                        <button
                          type="button"
                          onClick={() => togglePassword(0)}
                          className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-400 hover:text-gray-600 dark:text-gray-400"
                        >
                          {showPasswords[0] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('sysSettings.emailFromAddress')}
                      </label>
                      <input
                        type="email"
                        value={emailData.from_email}
                        onChange={(e) => setEmailData({...emailData, from_email: e.target.value})}
                        className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent min-h-[44px]"
                        placeholder="noreply@company.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('sysSettings.emailFromName')}
                      </label>
                      <input
                        type="text"
                        value={emailData.from_name}
                        onChange={(e) => setEmailData({...emailData, from_name: e.target.value})}
                        className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent min-h-[44px]"
                        placeholder="IT Toolbox System"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('sysSettings.emailDailyLimitField')}
                      </label>
                      <input
                        type="number"
                        value={emailData.daily_limit}
                        onChange={(e) => setEmailData({...emailData, daily_limit: parseInt(e.target.value) || 1000})}
                        className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent min-h-[44px]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('sysSettings.emailMonthlyLimitField')}
                      </label>
                      <input
                        type="number"
                        value={emailData.monthly_limit}
                        onChange={(e) => setEmailData({...emailData, monthly_limit: parseInt(e.target.value) || 30000})}
                        className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent min-h-[44px]"
                      />
                    </div>
                  </div>

                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={emailData.is_default}
                        onChange={(e) => setEmailData({...emailData, is_default: e.target.checked})}
                        className="rounded border-gray-300 dark:border-gray-600 text-green-600 shadow-sm focus:border-green-300 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{t('sysSettings.emailIsDefault')}</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={emailData.is_active}
                        onChange={(e) => setEmailData({...emailData, is_active: e.target.checked})}
                        className="rounded border-gray-300 dark:border-gray-600 text-green-600 shadow-sm focus:border-green-300 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{t('sysSettings.emailIsActive')}</span>
                    </label>
                  </div>
                </>
              )}

              {modalType === 'cron' && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('sysSettings.cronJobName')}
                      </label>
                      <input
                        type="text"
                        value={cronData.job_name}
                        onChange={(e) => setCronData({...cronData, job_name: e.target.value})}
                        className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent min-h-[44px]"
                        placeholder={t('sysSettings.cronJobNamePlaceholder')}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('sysSettings.cronExpressionField')}
                      </label>
                      <input
                        type="text"
                        value={cronData.cron_expression}
                        onChange={(e) => setCronData({...cronData, cron_expression: e.target.value})}
                        className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent min-h-[44px]"
                        placeholder="0 2 * * *"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {t('sysSettings.cronExpressionHint')}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('sysSettings.cronCommandField')}
                    </label>
                    {/* Güvenlik: serbest komut yerine sadece izin verilen betikler seçilebilir */}
                    <select
                      value={cronData.script}
                      onChange={(e) => setCronData({...cronData, script: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      <option value="">
                        {editingItem && !cronData.script ? t('sysSettings.cronKeepCommand') : t('sysSettings.cronSelectScript')}
                      </option>
                      {cronScripts.map(s => (
                        <option key={s.script} value={s.script}>{s.label} ({s.script})</option>
                      ))}
                    </select>
                    {editingItem && !cronData.script && cronData.command && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 font-mono break-all">
                        {cronData.command}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('sysSettings.cronDescField')}
                    </label>
                    <textarea
                      value={cronData.description}
                      onChange={(e) => setCronData({...cronData, description: e.target.value})}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('sysSettings.cronEmailNotif')}
                    </label>
                    <div className="max-h-32 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md p-2 bg-gray-50 dark:bg-gray-700">
                      {adminUsers.length > 0 ? (
                        adminUsers.map((user) => (
                          <label key={user.id} className="flex items-center py-1">
                            <input
                              type="checkbox"
                              checked={cronData.notification_recipients.includes(user.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setCronData({
                                    ...cronData,
                                    notification_recipients: [...cronData.notification_recipients, user.id]
                                  });
                                } else {
                                  setCronData({
                                    ...cronData,
                                    notification_recipients: cronData.notification_recipients.filter(id => id !== user.id)
                                  });
                                }
                              }}
                              className="rounded border-gray-300 dark:border-gray-600 text-orange-600 shadow-sm focus:border-orange-300 focus:ring focus:ring-orange-200 focus:ring-opacity-50 mr-2"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {user.name} ({user.role}) - {user.email}
                            </span>
                          </label>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400 italic">{t('sysSettings.cronAdminLoading')}</p>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {t('sysSettings.cronEmailNotifHint')}
                    </p>
                  </div>

                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={cronData.is_active}
                        onChange={(e) => setCronData({...cronData, is_active: e.target.checked})}
                        className="rounded border-gray-300 dark:border-gray-600 text-orange-600 shadow-sm focus:border-orange-300 focus:ring focus:ring-orange-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{t('sysSettings.cronIsActive')}</span>
                    </label>
                  </div>

                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-yellow-800 mb-2">{t('sysSettings.cronHelp')}</h4>
                    <div className="text-xs text-yellow-700 space-y-1">
                      <div><code>* * * * *</code> - {t('sysSettings.cronHelpFields')}</div>
                      <div><code>0 2 * * *</code> - {t('sysSettings.cronHelpEx1')}</div>
                      <div><code>0 */6 * * *</code> - {t('sysSettings.cronHelpEx2')}</div>
                      <div><code>0 0 * * 0</code> - {t('sysSettings.cronHelpEx3')}</div>
                      <div><code>0 0 1 * *</code> - {t('sysSettings.cronHelpEx4')}</div>
                    </div>
                  </div>
                </>
              )}

              {modalType === 'api' && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('sysSettings.apiName')}
                      </label>
                      <input
                        type="text"
                        value={apiData.key_name}
                        onChange={(e) => setApiData({...apiData, key_name: e.target.value})}
                        className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent min-h-[44px]"
                        placeholder="Mobile App API"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('sysSettings.apiRateLimitField')}
                      </label>
                      <input
                        type="number"
                        value={apiData.rate_limit_per_hour}
                        onChange={(e) => setApiData({...apiData, rate_limit_per_hour: parseInt(e.target.value) || 1000})}
                        className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent min-h-[44px]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('sysSettings.apiExpiryDate')}
                      </label>
                      <input
                        type="date"
                        value={apiData.expires_at}
                        onChange={(e) => setApiData({...apiData, expires_at: e.target.value})}
                        className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent min-h-[44px]"
                      />
                    </div>

                    <div className="flex items-center">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={apiData.is_active}
                          onChange={(e) => setApiData({...apiData, is_active: e.target.checked})}
                          className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{t('sysSettings.apiIsActive')}</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('sysSettings.apiPermissionsField')}
                    </label>
                    {apiData.permissions.map((permission, index) => (
                      <div key={index} className="flex items-center space-x-2 mb-2">
                        <input
                          type="text"
                          value={permission}
                          onChange={(e) => updateEmailInList(index, e.target.value, apiData.permissions, (list) => setApiData({...apiData, permissions: list}))}
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          placeholder="read:orders"
                        />
                        {apiData.permissions.length > 1 && (
                          <button
                            onClick={() => removeEmailFromList(index, apiData.permissions, (list) => setApiData({...apiData, permissions: list}))}
                            className="p-2 text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      onClick={() => addEmailToList(apiData.permissions, (list) => setApiData({...apiData, permissions: list}))}
                      className="text-sm text-indigo-600 hover:text-indigo-800"
                    >
                      {t('sysSettings.apiAddPermission')}
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('sysSettings.apiAllowedIps')}
                    </label>
                    {apiData.allowed_ips.map((ip, index) => (
                      <div key={index} className="flex items-center space-x-2 mb-2">
                        <input
                          type="text"
                          value={ip}
                          onChange={(e) => updateEmailInList(index, e.target.value, apiData.allowed_ips, (list) => setApiData({...apiData, allowed_ips: list}))}
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          placeholder="192.168.1.0/24"
                        />
                        {apiData.allowed_ips.length > 1 && (
                          <button
                            onClick={() => removeEmailFromList(index, apiData.allowed_ips, (list) => setApiData({...apiData, allowed_ips: list}))}
                            className="p-2 text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      onClick={() => addEmailToList(apiData.allowed_ips, (list) => setApiData({...apiData, allowed_ips: list}))}
                      className="text-sm text-indigo-600 hover:text-indigo-800"
                    >
                      {t('sysSettings.apiAddIp')}
                    </button>
                  </div>

                  {!editingItem && (
                    <div className="bg-primary/10 p-4 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-primary-dark">{t('sysSettings.apiAutoGenerate')}</span>
                        <button
                          onClick={generateApiKey}
                          className="text-sm text-primary hover:text-primary-dark"
                        >
                          {t('sysSettings.apiPreview')}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 sticky bottom-0 bg-white dark:bg-gray-800">
              <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="w-full sm:w-auto px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
                >
                  {t('sysSettings.cancel')}
                </button>
                <button
                  onClick={handleSave}
                  className="w-full sm:w-auto px-4 py-3 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors min-h-[44px] text-sm sm:text-base"
                >
                  {editingItem ? t('sysSettings.update') : t('sysSettings.save')}
                </button>
              </div>
            </div>
          </div>
        </Modal>

      {/* Sounds Tab */}
      {activeTab === 'sounds' && (
        <div className="space-y-6">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('sysSettings.soundsTitle')}</h2>

          {/* Ses Açma/Kapama ve Stil */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-6">

            {/* Enable toggle */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('sysSettings.soundEnabled')}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('sysSettings.soundEnabledDesc')}</p>
              </div>
              <button
                onClick={() => { soundManager.setEnabled(!soundSettings.enabled); setSoundSettings(soundManager.getSettings()); }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${soundSettings.enabled ? 'bg-primary' : 'bg-gray-200'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${soundSettings.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            {soundSettings.enabled && (
              <>
                {/* Volume */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('sysSettings.soundVolume')}: {Math.round(soundSettings.volume * 100)}%
                  </label>
                  <input
                    type="range" min="0" max="1" step="0.1"
                    value={soundSettings.volume}
                    onChange={(e) => { soundManager.setVolume(parseFloat(e.target.value)); setSoundSettings(soundManager.getSettings()); }}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>{t('sysSettings.soundVolumeLow')}</span>
                    <span>{t('sysSettings.soundVolumeHigh')}</span>
                  </div>
                </div>

                {/* Sound Style */}
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('sysSettings.soundStyle')}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{t('sysSettings.soundStyleDesc')}</p>
                  <div className="grid grid-cols-3 gap-3">
                    {([
                      { value: 'whatsapp',  label: 'WhatsApp',  emoji: '💬' },
                      { value: 'msn',       label: 'MSN',       emoji: '🟢' },
                      { value: 'messenger', label: 'Messenger', emoji: '💙' },
                    ] as const).map(({ value, label, emoji }) => (
                      <button
                        key={value}
                        onClick={() => {
                          soundManager.setSoundStyle(value);
                          setSoundSettings(soundManager.getSettings());
                          setTimeout(() => soundManager.testSound(), 50);
                        }}
                        className={`flex flex-col items-center justify-center py-3 px-2 rounded-lg border-2 transition-colors text-sm font-medium ${
                          soundSettings.soundStyle === value
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300'
                        }`}
                      >
                        <span className="text-xl mb-1">{emoji}</span>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Test */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => soundManager.testSound()}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                  >
                    <Volume2 className="w-4 h-4 mr-2" />
                    {t('sysSettings.soundTest')}
                  </button>
                  <span className="text-xs text-gray-500">{t('sysSettings.soundTestDesc')}</span>
                </div>
              </>
            )}

            {/* Typing sound */}
            <div className="pt-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('sysSettings.soundTyping')}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('sysSettings.soundTypingDesc')}</p>
              </div>
              <button
                onClick={() => { soundManager.setTypingSoundEnabled(!soundSettings.typingSoundEnabled); setSoundSettings(soundManager.getSettings()); }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${soundSettings.typingSoundEnabled ? 'bg-primary' : 'bg-gray-200'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${soundSettings.typingSoundEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            <div className="bg-primary/10 dark:bg-primary/20 p-3 rounded-lg">
              <p className="text-xs text-primary-dark">💡 <strong>{t('common.tip') || 'İpucu'}:</strong> {t('sysSettings.soundTip')}</p>
            </div>
          </div>

          {/* Custom Sound Files */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Music className="w-4 h-4" />
                  {t('sysSettings.soundCustomFiles')}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{t('sysSettings.soundCustomFilesDesc')}</p>
              </div>
              <div>
                <input ref={soundFileInputRef} type="file" accept=".mp3,.ogg,.wav,audio/*" className="hidden" onChange={handleSoundUpload} />
                <button
                  onClick={() => soundFileInputRef.current?.click()}
                  disabled={uploadingSound}
                  className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm disabled:opacity-50"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploadingSound ? t('sysSettings.soundUploading') : t('sysSettings.soundUpload')}
                </button>
              </div>
            </div>

            {/* Active custom sound indicator */}
            {activeSound && (
              <div className="flex items-center justify-between bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg px-4 py-2">
                <span className="text-sm text-green-800 dark:text-green-300">
                  ✅ {t('sysSettings.soundActive')}: <strong>{activeSound}</strong>
                </span>
                <button
                  onClick={() => handleSetActiveSound(null)}
                  className="text-xs text-gray-500 hover:text-red-600 transition-colors"
                >
                  {t('sysSettings.soundSetDefault')}
                </button>
              </div>
            )}

            {/* File list */}
            {soundFiles.length === 0 ? (
              <div className="text-center py-8 text-gray-400 dark:text-gray-500 text-sm">
                <Music className="w-8 h-8 mx-auto mb-2 opacity-40" />
                {t('sysSettings.soundNoFiles')}
              </div>
            ) : (
              <div className="space-y-2">
                {soundFiles.map((file: any) => {
                  const fileUrl = `${import.meta.env.VITE_API_URL?.replace('/api', '')}/uploads/sounds/${file.filename}`;
                  const isActive = activeSound === file.filename;
                  return (
                    <div
                      key={file.filename}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                        isActive
                          ? 'border-green-300 dark:border-green-600 bg-green-50 dark:bg-green-900/20'
                          : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50'
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Music className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-green-600' : 'text-gray-400'}`} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{file.filename}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {t('sysSettings.soundFileSize')}: {(file.size / 1024).toFixed(1)} KB
                            {' · '}{new Date(file.createdAt).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                        <button
                          onClick={() => handlePreviewSound(fileUrl)}
                          className="p-1.5 text-gray-500 hover:text-primary transition-colors"
                          title={t('sysSettings.soundPreview')}
                        >
                          <Play className="w-4 h-4" />
                        </button>
                        {!isActive ? (
                          <button
                            onClick={() => handleSetActiveSound(file.filename)}
                            className="px-3 py-1 text-xs bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
                          >
                            {t('sysSettings.soundSetActive')}
                          </button>
                        ) : (
                          <span className="px-3 py-1 text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-md font-medium">
                            {t('sysSettings.soundActive')}
                          </span>
                        )}
                        <button
                          onClick={() => handleDeleteSound(file.filename)}
                          className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                          title={t('sysSettings.soundDelete')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default SystemSettingsPage;